import { useReducer, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { wf, saveViewState } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { getFirst } from '../outline/utils.js'
import { getSheetRevisions, updateSheet, uploadRevisionPdf, uploadPdfToStorage, removeRevisionPdf, updateRevision, updateSheetRevisionQC, getOfficialSheetPdf, getProjectSheets, getSheetTemplates, getMilestoneSheetRevisions } from '../services/sheets.js'
import { getIssuedPdfMap } from '../services/sheetIssuance.js'
import { getPdfInsertTypes, getSheetPdfInsert, createSheetPdfInsert, updateSheetPdfInsert, syncPdfInsertConcept } from '../services/pdfInserts.js'
import { getTemplateImage, uploadTemplateImage } from '../services/templateImages.js'
import { getProjectAddressForVicinityMap, generateVicinityMapImage } from '../services/vicinityMap.js'
import { getRevisionQcSummaries, createOrGetVersion, qcSheetStatus, scrubRevisionQc, overrideApproveRevision, overrideReplacePdf } from '../services/qcVersions.js'
import { isManagerOrAbove, isAdmin } from '../services/tenancy.js'
import { getSheetChecklistItems, updateSheetChecklistItem, resetSheetChecklist, fieldPfx, getProjectSheetChecklistItems, openSheetChecklistCount } from '../services/checklist.js'
import { getSheetDraftAssignments, setSheetDraftAssignment, clearSheetDraftAssignment } from '../services/sheetAssignments.js'
import { getTeamMembers } from '../services/members.js'
import { getMemberName } from '../outline/utils.js'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider } from './ActionMenu.jsx'
import SlackThreadPanel from './SlackThreadPanel.jsx'
import RemoveSheetModal from './RemoveSheetModal.jsx'
import { alertDialog, confirmDialog, promptDialog } from './Dialog.jsx'
import { usePdfPagePicker } from './PdfPagePickerModal.jsx'
import SearchablePicker from './SearchablePicker.jsx'

/* ── Discipline helpers — single source of truth ────────────────────────── */

// Sheet-index order: the firm's standard discipline sequence for a drawing set. Used by
// groupByDiscipline() below as the FALLBACK ordering when a project's sheets don't carry a
// `Sheet Discipline Sort Order` value (that Airtable-configured field, when present, wins —
// see groupByDiscipline's comment) — this is the last-resort fixed order, no longer alphabetical
// or entry-order. The 7 named disciplines lead in the firm's specified sequence; the remaining
// real disciplines are appended after so sheets in them still group sensibly instead of being
// dropped. Anything not in this array at all falls to the very end (see the indexOf/999 fallback
// in groupByDiscipline) rather than throwing.
export const DISCIPLINE_ORDER = [
  'General', 'Special General', 'Civil', 'Landscape', 'As-Built', 'Demolition', 'Architectural', 'Structural',
  'Architectural Detailing', 'Interior Architecture', 'Energy',
  'Electrical', 'Mechanical', 'Plumbing', 'Other',
]

export function getDiscipline(title) {
  const m = /^([A-Za-z]+)/.exec((title || '').trim())
  const p = m ? m[1].toUpperCase() : ''
  if (p === 'FD')  return 'General'
  if (p === 'SP')  return 'Special General'
  if (p === 'AB')  return 'As-Built'
  if (p === 'AD')  return 'Architectural Detailing'
  if (p === 'EXT') return 'Architectural Detailing'
  if (p === 'INT') return 'Interior Architecture'
  if (p === 'EL')  return 'Electrical'
  if (p === 'G')   return 'General'
  if (p === 'A')   return 'Architectural'
  if (p === 'I')   return 'Interior Architecture'
  if (p === 'S')   return 'Structural'
  if (p === 'C')   return 'Civil'
  if (p === 'M')   return 'Mechanical'
  if (p === 'L')   return 'Landscape'
  if (p === 'D')   return 'Demolition'
  if (p === 'P')   return 'Plumbing'
  return 'Other'
}

export function sheetProgColor(prog) {
  return prog === 'Complete' ? '#1D9E75' : prog === 'In Progress' ? '#1a6fa8' : '#7a9aaa'
}

// Opens the QC Markup tool in its own browser tab (docs/QC_WORKFLOW.md) — the /qc route
// (QcPage.jsx) re-dispatches the same 'open-qc-markup' event QcMarkupModal listens for,
// sourced from these URL params instead of a live sheet object.
export function openQcTab(sheet, milestonePhasePrefix, milestoneId) {
  const qs = new URLSearchParams({ sheetId: sheet.id })
  if (sheet.revisionId) qs.set('revisionId', sheet.revisionId)
  if (milestoneId) qs.set('milestoneId', milestoneId)
  if (milestonePhasePrefix) qs.set('phasePrefix', milestonePhasePrefix)
  if (sheet.title) qs.set('title', sheet.title)
  window.open('/qc?' + qs.toString(), '_blank', 'noopener,noreferrer')
}

/* ── Internal helpers ────────────────────────────────────────────────────── */

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

function fmtDate(iso) {
  if (!iso) return ''
  const p = String(iso).split(/[-T]/g)
  if (p.length < 3) return iso
  return MONTHS[parseInt(p[1], 10) - 1] + ' ' + parseInt(p[2], 10) + ', ' + p[0]
}

// Group + order sheets to match the firm's reference list (examples/drawing_sheets.html):
//   • group by the actual `Discipline` field when present (fall back to sheet-number prefix detection),
//   • order the discipline GROUPS by `Sheet Discipline Sort Order` (min per group) — not a fixed list,
//   • order sheets WITHIN a group by `Drawing Sort Order`/`Sheet Sort Order` (numeric-aware), so e.g.
//     FD/S/SD/SGN/WSWH lands in the firm's intended order rather than raw sheet-number alpha.
// All three come from the normalized sheet shape (discipline / disciplineSort / sortOrder); when a
// caller doesn't supply them this degrades to the legacy prefix + DISCIPLINE_ORDER behavior.
function groupByDiscipline(sheets) {
  const map = {}
  const dispSort = {}
  ;(sheets || []).forEach(s => {
    const d = (s.discipline && String(s.discipline).trim()) || getDiscipline(s.title)
    ;(map[d] = map[d] || []).push(s)
    const ds = (s.disciplineSort != null && isFinite(Number(s.disciplineSort))) ? Number(s.disciplineSort) : 9999
    if (dispSort[d] === undefined || ds < dispSort[d]) dispSort[d] = ds
  })
  const cmpOrder = (a, b) => {
    const ao = a.sortOrder != null ? String(a.sortOrder) : ''
    const bo = b.sortOrder != null ? String(b.sortOrder) : ''
    if (ao || bo) return ao.localeCompare(bo, undefined, { numeric: true, sensitivity: 'base' })
    return String(a.title || '').localeCompare(String(b.title || ''), undefined, { numeric: true, sensitivity: 'base' })
  }
  Object.keys(map).forEach(d => map[d].sort(cmpOrder))
  const disciplines = Object.keys(map)
  disciplines.sort((a, b) => {
    const sa = dispSort[a], sb = dispSort[b]
    const hasA = sa !== undefined && sa !== 9999, hasB = sb !== undefined && sb !== 9999
    if (hasA && hasB) return sa - sb
    if (hasA) return -1
    if (hasB) return 1
    const ia = DISCIPLINE_ORDER.indexOf(a), ib = DISCIPLINE_ORDER.indexOf(b)
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib)
  })
  return disciplines.map(d => ({ discipline: d, items: map[d] }))
}

function getPdfUrl(rev) {
  const pdf = rev?.fields?.PDF
  return Array.isArray(pdf) && pdf.length ? pdf[0].url : null
}

// The Sheet Revision id the caller should treat as "current" for a normalized sheet — checks the
// revision directly on the sheet first (milestone context: sheet.revision or sheet.revisionId),
// then falls back to sheetRevCache[0] (production context — already Posted-Date-sorted, see
// getSheetRevisions). Always pass this to getOfficialSheetPdf so it scopes the QC-approved lookup
// to this exact revision instead of comparing approval timestamps across a sheet's other revisions.
function getCurrentRevisionId(s) {
  if (s?.revision?.id) return s.revision.id
  if (s?.revisionId) return s.revisionId
  const rev = ctx.sheetRevCache?.[s?.id]?.[0]
  return rev ? rev.id : null
}

// The full current-revision RECORD (not just its id) — same resolution order as
// getCurrentRevisionId, needed to read Posted Date (from Deliverables) off it. Falls back to null
// (never blocks selection) when only a bare revisionId string is available with no full record —
// e.g. some milestone-context rows only carry that — since we can't determine posted status
// without an extra fetch there and shouldn't guess.
function getCurrentRevisionRecord(s) {
  if (s?.revision) return s.revision
  return ctx.sheetRevCache?.[s?.id]?.[0] || null
}

// Whether this sheet's current revision belongs to a POSTED milestone — i.e. there's an actual
// released version of this sheet to export, not just an in-progress draft. `Posted Date (from
// Deliverables)` is blank on an unposted milestone (the same signal the titleblock base-walk and
// issued-PDF resolution already rely on — see docs/TITLEBLOCK_ISSUANCE.md). A sheet whose only
// revision ever sits under a not-yet-posted milestone (e.g. a brand-new sheet added mid-delta)
// has nothing official to export yet. Returns true (never blocks) when the revision record isn't
// available at all — see getCurrentRevisionRecord.
function isSheetExportable(s) {
  const rev = getCurrentRevisionRecord(s)
  if (!rev) return true
  const d = rev.fields?.['Posted Date (from Deliverables)']
  return !!(Array.isArray(d) ? d[0] : d)
}

// QC sheet status comes from the shared qcSheetStatus() helper (services/qcVersions.js) so the
// outline badge and the QC tool's sheet list render identical status + counts.

// "Did you mean the other milestone?" export nudge (2026-07-30, Matt). Purely a warning — never
// changes which PDF gets merged (that stays scoped to the milestone actually being exported, per
// the 2026-07-22 stale-export fix in getOfficialSheetPdf). Failure mode this guards against: a
// team member finishes and approves QC on a LATER revision round but forgets to post that
// milestone, then exports from wherever they happen to be zoomed and silently gets an older,
// unapproved drawing set instead. Flags a DIFFERENT, still-unposted milestone only when it shares
// at least one of the sheets being exported (so an unrelated fully-approved milestone elsewhere in
// the project never trips this) AND every one of that milestone's own sheets already has an
// approved QC round (not just some — a partially-reviewed sibling isn't "the real finished set").
async function findFullyApprovedUnpostedSibling(excludeMsId, currentSheetIds) {
  const candidates = (ctx.state?.milestones || []).filter(m => m.id !== excludeMsId && !m.fields?.['Posted Date'])
  const checks = await Promise.all(candidates.map(async m => {
    let revisions
    try { revisions = await getMilestoneSheetRevisions(m.id) } catch { return null }
    if (!revisions || !revisions.length) return null
    const sheetIdsOnM = new Set()
    revisions.forEach(r => (r.fields?.['Drawing Sheet'] || []).forEach(id => sheetIdsOnM.add(id)))
    const overlaps = [...sheetIdsOnM].some(id => currentSheetIds.has(id))
    if (!overlaps) return null
    const revIds = revisions.map(r => r.id)
    let summaries
    try { summaries = await getRevisionQcSummaries(revIds) } catch { return null }
    if (!revIds.every(id => summaries[id]?.approved)) return null
    return { name: m.fields['Full Deliverable Name'] || m.fields['Name'] || 'Untitled milestone', count: revIds.length }
  }))
  return checks.find(Boolean) || null
}

/* ── PDF icon SVG ────────────────────────────────────────────────────────── */
function PdfIcon() {
  return (
    <svg width="12" height="14" viewBox="0 0 12 14" fill="none" style={{ display: 'block' }}>
      <rect x="0.5" y="0.5" width="11" height="13" rx="1.5" stroke="currentColor" strokeWidth="1"/>
      <line x1="2.5" y1="4.5" x2="9.5" y2="4.5" stroke="currentColor" strokeWidth="1"/>
      <line x1="2.5" y1="6.5" x2="9.5" y2="6.5" stroke="currentColor" strokeWidth="1"/>
      <line x1="2.5" y1="8.5" x2="6.5" y2="8.5" stroke="currentColor" strokeWidth="1"/>
    </svg>
  )
}

/* ── Revision type config ─────────────────────────────────────────────────── */
const REV_TYPES = ['Original Release', 'Phase Advancement', 'Plan Check Revision', 'Permit Revision', 'ASI Revision']

const REV_TYPE_STYLE = {
  'Original Release':              { bg: '#e8f5f0', color: '#1D9E75',  label: 'Original' },
  'Phase Advancement':             { bg: '#e8f1f8', color: '#1a6fa8',  label: 'Advancement' },
  'Plan Check Revision':           { bg: '#fef3e8', color: '#c87941',  label: 'PC Revision' },
  'Permit Revision':               { bg: '#fef6e8', color: '#b8640a',  label: 'Permit Rev' },
  'ASI Revision':                  { bg: '#f1ecf8', color: '#6b4fa0',  label: 'ASI' },
  /* Legacy value — display only, not selectable */
  'Plan Check or Permit Revision': { bg: '#fef3e8', color: '#c87941',  label: 'Revision' },
}

/* ── RevisionRow — one line in the expanded revision area ────────────────── */
// canManagePdf — upload/remove the drawing PDF. OFF by default: only milestone-context call
// sites enable it. The Production branch is a VIEWER of the drawings built in the milestones —
// it never uploads or removes them (and its rows have no milestone phase to gate a checklist
// against anyway).
function RevisionRow({ revision, isLatest, showMilestone, revisionId, milestonePhasePrefix = '', canManagePdf = false, inProgressBadge = false }) {
  const [localType,      setLocalType]      = useState(revision.fields['Type'] || '')
  const [editingType,    setEditingType]    = useState(false)
  const type    = localType
  const ts      = REV_TYPE_STYLE[type] || { bg: '#f4f7f8', color: '#7a9aaa', label: type || '—' }
  const msName  = getFirst(revision.fields['Full Deliverable Name (from Deliverables)']) || ''
  const dateRaw = getFirst(revision.fields['Posted Date (from Deliverables)']) || ''
  const date    = fmtDate(dateRaw)
  const fileRef = useRef(null)
  const { resolveUpload, picker: pagePicker } = usePdfPagePicker()

  const [localPdfUrl,    setLocalPdfUrl]    = useState(getPdfUrl(revision))
  const [uploading,      setUploading]      = useState(false)
  const [uploadErr,      setUploadErr]      = useState(null)
  const [confirmRemove,  setConfirmRemove]  = useState(false)
  const [removing,       setRemoving]       = useState(false)
  // Narrative editing
  const [editingSummary, setEditingSummary] = useState(false)
  const [summaryDraft,   setSummaryDraft]   = useState(revision.fields['Summary of Change'] || '')
  const summaryVal = revision.fields['Summary of Change'] || ''

  // Once QC comments (AI or manual) exist — or the drawing has been approved — the PDF is the
  // QC process's single source of truth; removing it would corrupt an in-progress round. Read
  // from the already-loaded ctx.qcSummaryCache (bulk-loaded by DrawingSheetList) so the button
  // itself reflects this without an extra fetch; handleRemovePdf re-checks authoritatively too.
  const qcSm = ctx.qcSummaryCache?.[revisionId]
  const qcLocksPdf = !!(qcSm && ((qcSm.total || 0) > 0 || qcSm.approved))

  // Gate EVERY upload (first PDF or a replacement) on the sheet's milestone checklist — no
  // drawing goes in until every in-phase item is Complete or Not Required. Runs on the BUTTON
  // click, BEFORE the file picker opens, so the user isn't sent hunting for a file only to be
  // refused afterward. Returns true when blocked (and shows the dialog). The checklist is warm
  // in the loader cache in practice (the row's progress count reads from it), so the pre-check
  // doesn't delay the picker. (Milestone context only; the phase prefix scopes the items.)
  async function checklistBlocksUpload() {
    if (!milestonePhasePrefix) return false
    const dsId = getFirst(revision.fields?.['Drawing Sheet']) || getFirst(revision.fields?.['Record ID (from Drawing Sheet)'])
    if (!dsId) return false
    try {
      const cached = ctx.loader.get('sheetCl:' + dsId)
      const open = await openSheetChecklistCount(dsId, milestonePhasePrefix, Array.isArray(cached) ? cached : undefined)
      if (open) {
        alertDialog({ title: 'Checklist incomplete', message: 'The sheet’s checklist for this milestone must be completed before a PDF can be uploaded.\n\n' + open + ' item' + (open !== 1 ? 's' : '') + ' still need' + (open === 1 ? 's' : '') + ' to be marked Complete or Not Required.' })
        return true
      }
    } catch { /* if the checklist can't be read, don't block the upload */ }
    return false
  }

  async function handleFile(e) {
    const rawFile = e.target.files?.[0]
    if (!rawFile) return
    // A Sheet Revision is ONE drawing PDF — if the file has more than one page, the user picks
    // which one they meant; the rest are extracted away client-side and never uploaded.
    // Single-page PDFs pass straight through unchanged.
    const file = await resolveUpload(rawFile)
    if (fileRef.current) fileRef.current.value = ''
    if (!file) return
    setUploading(true); setUploadErr(null)
    try {
      const url = await uploadRevisionPdf(revisionId, file)
      setLocalPdfUrl(url)
      if (revision.fields) revision.fields.PDF = [{ url, filename: file.name }]
      window.dispatchEvent(new Event('outline-render'))
      // Uploading a PDF initiates the QC round (same as opening the tool once), so the
      // sheet moves from "No PDF" to "in review" without anyone opening the markup tool.
      try {
        const drawingSheetId = getFirst(revision.fields?.['Drawing Sheet']) || getFirst(revision.fields?.['Record ID (from Drawing Sheet)'])
        if (drawingSheetId) {
          await createOrGetVersion({ drawingSheetId, sheetRevisionId: revisionId, basePdfUrl: url })
          window.dispatchEvent(new CustomEvent('qc-updated', { detail: { sheetId: drawingSheetId } }))
        }
      } catch (qErr) { /* non-fatal — the round can still be started by opening the tool */ }
    } catch (err) {
      setUploadErr(err.message || 'Upload failed')
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  // QC-aware removal. Once QC comments (AI or manual) have been placed — or the drawing has been
  // approved — the PDF is the QC process's single source of truth; removing it mid-stream would
  // corrupt the round (and the QC tool's self-heal would just push its own stable copy back into
  // the attachment on next open anyway). The correct path from there is to finish the QC process
  // (replace the drawing through the markup tool, which carries comments forward) or delete the
  // whole Sheet record if that's genuinely what's needed — never a bare PDF removal.
  //   - no QC rounds            → plain remove (as before)
  //   - rounds but zero items   → remove + quietly delete the unworked round(s) (back to pre-round)
  //   - real QC work / approved → hard block, no override
  async function handleRemovePdf() {
    setRemoving(true)
    try {
      let sm = null
      try { sm = (await getRevisionQcSummaries([revisionId]))[revisionId] || null } catch { /* treat as unknown */ }
      const hasRounds = !!sm?.hasVersion || !!sm?.approved
      const items = sm?.total || 0
      if (items > 0 || sm?.approved) {
        alertDialog({
          title: 'Can’t remove this drawing',
          message: (sm?.approved
            ? 'This drawing has been QC-approved. '
            : items + ' QC item' + (items !== 1 ? 's' : '') + ' (AI or manual) have already been placed against it. ')
            + 'The PDF is the source of truth for an in-progress QC round and can’t be removed on its own — replace it through AlderQC (which carries the comments forward), or delete the whole Sheet record if that’s truly what’s needed.',
        })
        return
      }
      // Unworked round (QC initiated on upload but nothing recorded yet) — safe to scrub quietly.
      if (hasRounds) await scrubRevisionQc(revisionId, getFirst(revision.fields?.['Drawing Sheet']) || null)
      await removeRevisionPdf(revisionId)
      if (revision.fields) revision.fields.PDF = []
      setLocalPdfUrl(null)
      window.dispatchEvent(new CustomEvent('qc-updated', { detail: { sheetId: getFirst(revision.fields?.['Drawing Sheet']) || null } }))
      window.dispatchEvent(new Event('outline-render'))
    } catch (err) {
      console.error('PDF remove error', err)
      alertDialog({ title: 'Remove failed', message: err.message || 'Could not remove the drawing.' })
    } finally {
      setRemoving(false); setConfirmRemove(false)
    }
  }

  // Admin-only: swap the PDF of an already-approved drawing (e.g. a titleblock correction)
  // without reopening QC or deleting the revision. Uploads to Storage FIRST without touching
  // Airtable (uploadPdfToStorage) — overrideReplacePdf validates (aspect match, admin seat,
  // approved round exists) before it patches anything, so a rejected swap never leaves
  // Airtable pointing at a PDF the QC round doesn't agree with. See docs/QC_WORKFLOW.md.
  const adminFileRef = useRef(null)
  const [replacingPdf, setReplacingPdf] = useState(false)
  async function handleAdminReplaceFile(e) {
    const rawFile = e.target.files?.[0]
    if (adminFileRef.current) adminFileRef.current.value = ''
    if (!rawFile) return
    const file = await resolveUpload(rawFile)
    if (!file) return
    const ok = await confirmDialog({
      title: 'Replace approved PDF?',
      message: 'This drawing is QC-approved. Replacing its PDF is an admin override — the drawing stays approved (its QC history is preserved, not re-reviewed). Only use this when the underlying drawing hasn’t materially changed (e.g. a titleblock correction), not for real revisions.',
      confirmLabel: 'Replace PDF',
    })
    if (!ok) return
    const reason = await promptDialog({ title: 'Reason (optional)', message: 'Why is this PDF being replaced?', placeholder: 'e.g. titleblock correction' })
    if (reason === null) return
    setReplacingPdf(true)
    try {
      const drawingSheetId = getFirst(revision.fields?.['Drawing Sheet']) || getFirst(revision.fields?.['Record ID (from Drawing Sheet)'])
      const url = await uploadPdfToStorage(file)
      await overrideReplacePdf(drawingSheetId, revisionId, url, reason || undefined)
      setLocalPdfUrl(url)
      if (revision.fields) revision.fields.PDF = [{ url, filename: file.name }]
      if (ctx.qcSummaryCache) ctx.qcSummaryCache[revisionId] = undefined
      if (drawingSheetId) window.dispatchEvent(new CustomEvent('qc-updated', { detail: { sheetId: drawingSheetId } }))
      window.dispatchEvent(new Event('outline-render'))
    } catch (err) {
      alertDialog({ title: 'Could not replace PDF', message: err.message || 'Something went wrong.' })
    } finally {
      setReplacingPdf(false)
    }
  }

  async function saveSummary() {
    const val = summaryDraft.trim()
    setEditingSummary(false)
    if (val === (revision.fields['Summary of Change'] || '')) return
    revision.fields['Summary of Change'] = val
    window.dispatchEvent(new Event('outline-render'))
    try {
      await updateRevision(revisionId, { summary: val })
    } catch (err) {
      console.error('Summary save error', err)
    }
  }

  return (
    <>
      {pagePicker}
      <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 8,
      padding: '5px 8px 5px 49px',
      borderBottom: '0.5px solid var(--aj-border)',
    }}>
      {/* Type badge — click to change when revisionId is available */}
      {editingType ? (
        <select
          autoFocus
          value={localType}
          onChange={async e => {
            const newType = e.target.value
            setLocalType(newType)
            revision.fields['Type'] = newType
            setEditingType(false)
            if (revisionId) {
              try { await updateRevision(revisionId, { type: newType }) }
              catch(err) { console.error('[RevisionRow] type save error', err) }
            }
          }}
          onBlur={() => setEditingType(false)}
          onClick={e => e.stopPropagation()}
          style={{
            fontSize: 10, fontFamily: '"DM Mono", monospace',
            padding: '1px 3px', borderRadius: 3, flexShrink: 0,
            border: '0.5px solid var(--aj-border-mid)',
            background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)', outline: 'none',
          }}
        >
          {REV_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      ) : (
        <span
          onClick={revisionId ? e => { e.stopPropagation(); setEditingType(true) } : undefined}
          title={revisionId ? 'Click to change type' : undefined}
          style={{
            background: ts.bg, color: ts.color,
            fontSize: 9, fontFamily: '"DM Mono", monospace', fontWeight: 600,
            padding: '2px 5px', borderRadius: 3, flexShrink: 0, marginTop: 1,
            letterSpacing: '0.04em',
            cursor: revisionId ? 'pointer' : 'default',
          }}
        >
          {ts.label}
        </span>
      )}

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {showMilestone && msName && (
          <div style={{
            fontSize: 11.5, color: 'var(--aj-text)', fontFamily: '"DM Sans", sans-serif',
            fontWeight: 500, marginBottom: 1, lineHeight: 1.3,
          }}>
            {msName}
            {inProgressBadge && (
              <span style={{
                marginLeft: 6, fontFamily: '"DM Mono",monospace', fontWeight: 400, fontSize: 10,
                letterSpacing: '0.04em', textTransform: 'uppercase', color: '#d97706',
              }}>
                ⏳ In Progress
              </span>
            )}
          </div>
        )}

        {/* Narrative — editable when revisionId is present */}
        {revisionId ? (
          editingSummary ? (
            <textarea
              autoFocus
              value={summaryDraft}
              onChange={e => setSummaryDraft(e.target.value)}
              onBlur={saveSummary}
              onKeyDown={e => {
                if (e.key === 'Enter' && e.ctrlKey) { e.preventDefault(); saveSummary() }
                if (e.key === 'Escape') { e.stopPropagation(); setEditingSummary(false); setSummaryDraft(summaryVal) }
              }}
              rows={2}
              style={{
                width: '100%', fontFamily: '"DM Sans", sans-serif', fontSize: 11.5,
                color: 'var(--aj-text-mid)', lineHeight: 1.4, padding: '3px 5px',
                background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)',
                borderRadius: 3, resize: 'vertical', outline: 'none',
              }}
            />
          ) : (
            <div
              onClick={e => { e.stopPropagation(); setSummaryDraft(summaryVal); setEditingSummary(true) }}
              style={{
                fontSize: 11.5, lineHeight: 1.4, cursor: 'text',
                color: summaryVal ? 'var(--aj-text-mid)' : 'var(--aj-text-muted)',
                fontFamily: '"DM Sans", sans-serif',
                fontStyle: summaryVal ? 'normal' : 'italic',
              }}
              title="Click to edit change description"
            >
              {summaryVal || 'Add change description…'}
            </div>
          )
        ) : (
          summaryVal ? (
            <div style={{ fontSize: 11.5, color: 'var(--aj-text-mid)', fontFamily: '"DM Sans", sans-serif', lineHeight: 1.4 }}>
              {summaryVal}
            </div>
          ) : null
        )}

        {uploadErr && (
          <div style={{ fontSize: 11, color: '#c0392b', marginTop: 2 }}>{uploadErr}</div>
        )}
      </div>

      {/* Date */}
      {date && (
        <span style={{
          fontSize: 11, color: 'var(--aj-text-muted)', fontFamily: '"DM Mono", monospace',
          flexShrink: 0, marginTop: 1,
        }}>
          {date}
        </span>
      )}

      {/* LATEST badge */}
      {isLatest && (
        <span style={{
          fontSize: 9, fontWeight: 700, letterSpacing: '0.07em',
          color: '#1D9E75', flexShrink: 0, marginTop: 2, fontFamily: '"DM Mono", monospace',
        }}>
          LATEST
        </span>
      )}

      {/* PDF link — prefer this REVISION's own issued (posted) titleblock-overlay PDF, then its
          own QC-approved drawing, then the raw upload. Previously a static href straight to
          localPdfUrl (the raw attachment) regardless of posted/QC state — so this row's icon and
          the sheet-level collapsed icon (which already did this resolution) could show two
          different files for what's supposed to be the same revision (Matt, 2026-07-23).
          Gated on posted-or-editing (2026-07-23): a revision with no Posted Date is still mid-QC/
          pre-approval — offering an open/download icon for it in a read-only view (Reference/
          Production's prodInProgressRevisions rows) risks someone grabbing an unfinished drawing
          under the impression it's final, a version-control hazard. `canManagePdf` is true only at
          the milestone-context call sites — there the row IS the active working document (upload/
          replace/QC happen right here), so it stays viewable regardless of posted state. */}
      {localPdfUrl && (dateRaw || canManagePdf) && (
        <a
          href={localPdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={async e => {
            e.stopPropagation(); e.preventDefault()
            let url = localPdfUrl
            try {
              if (revisionId) {
                const m = await getIssuedPdfMap([revisionId])
                if (m[revisionId]) url = m[revisionId]
              }
              if (url === localPdfUrl) {
                const sheetIdForRev = getFirst(revision.fields['Drawing Sheet'])
                if (sheetIdForRev) {
                  const r = await getOfficialSheetPdf(sheetIdForRev, localPdfUrl, revisionId)
                  if (r.url) url = r.url
                }
              }
            } catch {}
            window.open(url, '_blank', 'noopener,noreferrer')
          }}
          style={{ flexShrink: 0, color: '#c0392b', display: 'flex', alignItems: 'center', marginTop: 1 }}
          title="Open PDF"
        >
          <PdfIcon />
        </a>
      )}

      {/* PDF remove (when PDF exists and revisionId available) — milestone context only */}
      {canManagePdf && revisionId && localPdfUrl && !removing && (
        confirmRemove ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, marginTop: 1 }}>
            <button
              onClick={e => { e.stopPropagation(); handleRemovePdf() }}
              style={{
                fontSize: 10, fontFamily: '"DM Sans", sans-serif', background: 'transparent',
                border: 'none', cursor: 'pointer', color: '#c0392b', padding: 0,
              }}
            >Remove?</button>
            <button
              onClick={e => { e.stopPropagation(); setConfirmRemove(false) }}
              style={{
                fontSize: 10, fontFamily: '"DM Sans", sans-serif', background: 'transparent',
                border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', padding: 0,
              }}
            >Cancel</button>
          </span>
        ) : (
          <button
            onClick={e => {
              e.stopPropagation()
              if (qcLocksPdf) {
                alertDialog({
                  title: 'Can’t remove this drawing',
                  message: (qcSm.approved
                    ? 'This drawing has been QC-approved. '
                    : (qcSm.total || 0) + ' QC item' + (qcSm.total !== 1 ? 's' : '') + ' (AI or manual) have already been placed against it. ')
                    + 'The PDF is the source of truth for an in-progress QC round and can’t be removed on its own — replace it through AlderQC (which carries the comments forward), or delete the whole Sheet record if that’s truly what’s needed.',
                })
                return
              }
              setConfirmRemove(true)
            }}
            title={qcLocksPdf ? (qcSm.approved ? 'Locked — this drawing is QC-approved' : 'Locked — QC comments have been placed against this drawing') : 'Remove PDF'}
            style={{
              flexShrink: 0, background: 'transparent', border: 'none', cursor: qcLocksPdf ? 'not-allowed' : 'pointer',
              color: qcLocksPdf ? 'var(--aj-border-mid)' : 'var(--aj-text-muted)', padding: '0 0 0 2px', marginTop: 1,
              fontSize: 11, lineHeight: 1,
            }}
          >✕</button>
        )
      )}
      {removing && (
        <span style={{ fontSize: 10, color: 'var(--aj-text-muted)', flexShrink: 0, marginTop: 1 }}>Removing...</span>
      )}

      {/* Admin override — swap an approved/locked PDF without deleting the revision (e.g. a
          titleblock correction). Only surfaces when the normal remove/replace path is locked.
          Blocked (disabled, with an explanation) while the milestone is posted — the red "open
          PDF" icon reads the burned/issued PDF once posted, not the qc_versions round directly,
          and burning only happens at post/re-post time. Replacing here while posted would
          silently succeed in the database while every visible icon/export kept showing the old
          drawing until an unpost+repost — exactly what happened with Knapp's G007 (Matt,
          2026-07-23). The server enforces this too (overrideReplacePdf rejects with a 409) —
          this is the friendlier client-side version of the same rule. */}
      {canManagePdf && revisionId && localPdfUrl && qcLocksPdf && isAdmin() && !replacingPdf && (
        dateRaw ? (
          <span
            title="This milestone is posted. Unpost it, replace the PDF, then re-post to burn the corrected drawing into the released set."
            style={{
              flexShrink: 0, color: 'var(--aj-text-muted)', fontSize: 10, fontFamily: '"DM Sans", sans-serif',
              display: 'flex', alignItems: 'center', gap: 3, marginTop: 1, marginLeft: 4, cursor: 'help',
            }}
          >
            Replace (Admin) — unpost first
          </span>
        ) : (
          <>
            <button
              onClick={e => { e.stopPropagation(); adminFileRef.current?.click() }}
              title="Admin override: replace this approved drawing's PDF"
              style={{
                flexShrink: 0, background: 'transparent', border: 'none', cursor: 'pointer',
                color: '#b8640a', fontSize: 10, fontFamily: '"DM Sans", sans-serif', padding: 0,
                display: 'flex', alignItems: 'center', gap: 3, marginTop: 1, marginLeft: 4,
              }}
            >
              Replace (Admin)
            </button>
            <input
              ref={adminFileRef} type="file" accept=".pdf,application/pdf"
              style={{ display: 'none' }} onChange={handleAdminReplaceFile}
            />
          </>
        )
      )}
      {replacingPdf && (
        <span style={{ fontSize: 10, color: 'var(--aj-text-muted)', flexShrink: 0, marginTop: 1 }}>Replacing...</span>
      )}

      {/* Upload button — only shown when no PDF exists, and only in milestone context */}
      {canManagePdf && revisionId && !localPdfUrl && !uploading && (
        <>
          <button
            onClick={async e => { e.stopPropagation(); if (await checklistBlocksUpload()) return; fileRef.current?.click() }}
            style={{
              flexShrink: 0, background: 'transparent', border: 'none', cursor: 'pointer',
              color: '#1a6fa8',
              fontSize: 10, fontFamily: '"DM Sans", sans-serif', padding: 0,
              display: 'flex', alignItems: 'center', gap: 3, marginTop: 1,
            }}
            title="Upload PDF"
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
              <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
            </svg>
            PDF
          </button>
          <input
            ref={fileRef} type="file" accept=".pdf,application/pdf"
            style={{ display: 'none' }} onChange={handleFile}
          />
        </>
      )}
      {uploading && (
        <span style={{ fontSize: 10, color: 'var(--aj-text-muted)', flexShrink: 0, marginTop: 1 }}>Uploading...</span>
      )}
    </div>
    </>
  )
}

/* ── Not-required reason options (mirrors milestone checklist) ───────────── */
const NOT_REQUIRED_REASONS = [
  'Not applicable to this project',
  'Handled in previous phase',
  'Client has waived this requirement',
  'Covered by consultant',
]

/* ── SheetClNotRequiredModal — reason prompt before marking N/R ──────────── */
function SheetClNotRequiredModal({ itemName, onConfirm, onCancel }) {
  const [selected, setSelected] = useState(null)
  const [custom,   setCustom]   = useState('')

  const canConfirm = selected !== null || custom.trim().length > 0
  const reason     = custom.trim() || selected || ''

  const btnBase = {
    border: 'none', borderRadius: 4, padding: '8px 18px',
    fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 500, cursor: 'pointer',
  }

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
        zIndex: 9001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div style={{
        background: 'var(--aj-bg-elevated)', borderRadius: 10, border: '0.5px solid var(--aj-border)',
        padding: '22px 24px', width: 360, maxWidth: '100%',
        boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
      }}>
        {/* Title */}
        <div style={{
          fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 12,
          letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)', marginBottom: 16,
        }}>
          Mark not required
        </div>

        {/* Item name */}
        <div style={{
          fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)',
          padding: '8px 10px', background: 'var(--aj-bg-subtle)', borderRadius: 4,
          fontWeight: 500, marginBottom: 16,
        }}>
          {itemName}
        </div>

        {/* Reason label */}
        <div style={{
          fontFamily: '"DM Sans",sans-serif', fontSize: 11, fontWeight: 500,
          letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 8,
        }}>
          Reason
        </div>

        {/* Reason pills */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {NOT_REQUIRED_REASONS.map(r => (
            <button
              key={r}
              onClick={() => { setSelected(selected === r ? null : r); setCustom('') }}
              style={{
                border: selected === r ? '1px solid #07272D' : '1px solid var(--aj-border-mid)',
                borderRadius: 20, padding: '5px 13px',
                fontSize: 12, fontFamily: '"DM Sans",sans-serif',
                background: selected === r ? '#07272D' : 'var(--aj-bg-elevated)',
                color: selected === r ? '#fff' : 'var(--aj-text)',
                cursor: 'pointer',
              }}
            >{r}</button>
          ))}
        </div>

        {/* Free-text reason */}
        <input
          type="text"
          placeholder="Or describe a reason…"
          value={custom}
          onChange={e => { setCustom(e.target.value); setSelected(null) }}
          style={{
            width: '100%', padding: '8px 12px', boxSizing: 'border-box',
            border: '0.5px solid var(--aj-border-mid)', borderRadius: 6,
            fontFamily: '"DM Sans",sans-serif', fontSize: 12, outline: 'none',
            color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)',
            marginBottom: 20,
          }}
        />

        {/* Footer */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{ ...btnBase, background: 'transparent', border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text-mid)' }}
          >
            Cancel
          </button>
          <button
            onClick={() => canConfirm && onConfirm(reason)}
            disabled={!canConfirm}
            style={{ ...btnBase, background: '#07272D', color: 'white', opacity: canConfirm ? 1 : 0.5 }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

/* ── Menu icon helpers — exact SVGs from outline/constants.js ────────────── */
/* ICO_CHECK: checkmark */
const IcoCheck = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <polyline points="2.5,8.5 6,12 13.5,4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)
/* ICO_SKIP: X inside circle (also used for "Not required" / "Mark required") */
const IcoSkip = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3"/>
    <line x1="5" y1="5" x2="11" y2="11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    <line x1="11" y1="5" x2="5" y2="11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
)

/* ── SheetClItemNode — outline-style checklist item ─────────────────────── */
/* Toggle a sheet checklist item's Complete status without a mounted SheetClItemNode instance —
   used by the global 'x' keyboard shortcut (outline.js), which only has DOM attributes to work
   with, not React props/closures. Mirrors handleComplete/handleUncomplete below: mutates the SAME
   cached record object the DataLoader holds (so the already-rendered tree picks up the change on
   the outline-render this dispatches), with rollback on failure. */
async function toggleSheetClItemComplete(itemId, sheetId, phasePrefix) {
  const arr = ctx.loader.get('sheetCl:' + sheetId)
  if (!Array.isArray(arr)) return
  const record = arr.find(i => i.id === itemId)
  if (!record) return
  const fp = fieldPfx(phasePrefix)
  const wasDone = !!record.fields[fp + ' Complete']
  const prevCompletedBy = record.fields[fp + ' Completed by']
  const memberId = wf.currentMemberId
  record.fields[fp + ' Complete'] = !wasDone
  record.fields[fp + ' Completed by'] = !wasDone && memberId ? [memberId] : []
  window.dispatchEvent(new Event('outline-render'))
  try {
    await updateSheetChecklistItem(itemId, phasePrefix, wasDone ? 'uncomplete' : 'complete', wasDone ? null : memberId)
  } catch (e) {
    record.fields[fp + ' Complete'] = wasDone
    record.fields[fp + ' Completed by'] = prevCompletedBy
    window.dispatchEvent(new Event('outline-render'))
    console.error('[toggleSheetClItemComplete] error', e)
  }
}
if (typeof window !== 'undefined') window.ajScToggleSheetClItem = toggleSheetClItemComplete

function SheetClItemNode({ item, sheetId, phasePrefix, currentMemberId, readOnly = false,
                           selectMode, isSelected, onToggleSelect, onItemCompleted = null }) {
  const menu               = useActionMenu()
  const [showNRModal, setShowNRModal] = useState(false)
  const fp        = fieldPfx(phasePrefix)
  const isDone    = !!item.fields[fp + ' Complete']
  const isSkipped = !!item.fields[fp + ' Not Required']
  const nrReason  = item.fields[fp + ' Not Required Reason'] || ''
  const rawName   = getFirst(item.fields['Name for List (from Deliverable Checklist Item Template)'])
                 || item.fields['Name'] || '—'
  // Strip the "(more...✚)" suffix — the expand triangle replaces this visual indicator
  const name      = rawName.replace(' (more...✚)', '').trim()
  const compId    = getFirst(item.fields[fp + ' Completed by']) || ''
  const compName  = compId ? getMemberName(compId) : ''

  // Expandable content from the template
  const summary       = item.fields['Summary (from Deliverable Checklist Item Template)'] || ''
  const exampleImages = item.fields['Example Image (from Deliverable Checklist Item Template)'] || []
  const hasContent    = !!(summary || (Array.isArray(exampleImages) && exampleImages.length > 0))
  const expandKey     = 'sheetClItem:' + item.id
  // Collapsed by default (=== false means expanded)
  const isExpanded    = hasContent && wf.collapsed[expandKey] === false

  function toggleExpand(e) {
    e.stopPropagation()
    wf.collapsed[expandKey] = isExpanded ? true : false
    window.dispatchEvent(new Event('outline-render'))
  }

  async function handleComplete() {
    menu.close()
    item.fields[fp + ' Complete'] = true
    if (currentMemberId) item.fields[fp + ' Completed by'] = [currentMemberId]
    onItemCompleted?.()
    window.dispatchEvent(new Event('outline-render'))
    try { await updateSheetChecklistItem(item.id, phasePrefix, 'complete', currentMemberId) }
    catch (e) { console.error('[SheetClItemNode] complete error', e) }
  }
  async function handleUncomplete() {
    menu.close()
    item.fields[fp + ' Complete'] = false
    item.fields[fp + ' Completed by'] = []
    window.dispatchEvent(new Event('outline-render'))
    try { await updateSheetChecklistItem(item.id, phasePrefix, 'uncomplete', null) }
    catch (e) { console.error('[SheetClItemNode] uncomplete error', e) }
  }
  async function handleSkip(reason) {
    setShowNRModal(false)
    menu.close()
    item.fields[fp + ' Not Required'] = true
    if (currentMemberId) item.fields[fp + ' Not Required Member'] = [currentMemberId]
    if (reason) item.fields[fp + ' Not Required Reason'] = reason
    onItemCompleted?.()
    window.dispatchEvent(new Event('outline-render'))
    try { await updateSheetChecklistItem(item.id, phasePrefix, 'notRequired', currentMemberId, reason) }
    catch (e) { console.error('[SheetClItemNode] skip error', e) }
  }
  async function handleMarkRequired() {
    menu.close()
    item.fields[fp + ' Not Required'] = false
    item.fields[fp + ' Not Required Member'] = []
    item.fields[fp + ' Not Required Reason'] = ''
    window.dispatchEvent(new Event('outline-render'))
    try { await updateSheetChecklistItem(item.id, phasePrefix, 'markRequired', null) }
    catch (e) { console.error('[SheetClItemNode] mark required error', e) }
  }

  return (
    <div
      className={'wf-node wf-node--item' + (isDone ? ' wf-node--done' : '') + (isSkipped ? ' wf-node--skipped' : '')}
      data-key={expandKey}
      {...(!readOnly ? {
        'data-cl-kind': 'sheet',
        'data-cl-item': item.id,
        'data-cl-sheetid': sheetId,
        'data-cl-phase': phasePrefix,
        'data-cl-skip': isSkipped ? 'true' : 'false',
      } : {})}
    >
      <div className="wf-row">
        {/* tri column — expand triangle when content exists; otherwise ⋯ or spacer */}
        {hasContent ? (
          <button className="wf-triangle" onClick={toggleExpand}>
            <span className={`wf-tri-icon ${isExpanded ? 'wf-tri-icon--down' : 'wf-tri-icon--right'}`}></span>
          </button>
        ) : !readOnly ? (
          <ActionMenuButton menu={menu} triangle />
        ) : (
          <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        )}
        {/* bull column */}
        {selectMode ? (
          <input
            type="checkbox"
            className="wf-item-cb"
            checked={isSelected}
            onChange={() => onToggleSelect && onToggleSelect(item.id)}
            onClick={e => e.stopPropagation()}
            style={{ accentColor: '#1D9E75' }}
          />
        ) : (
          <span className="wf-bullet wf-bullet--item" />
        )}
        {/* text column */}
        <div className="wf-text-wrap">
          <span className={'wf-node-text wf-node-text--item' + (isDone || isSkipped ? ' wf-node-text--done' : '')}>
            {name}
          </span>
          <span className="wf-meta">
            {/* Subtle indicator that this item has example images — visible before expanding */}
            {Array.isArray(exampleImages) && exampleImages.length > 0 && (
              <span
                title={exampleImages.length + ' example image' + (exampleImages.length > 1 ? 's' : '')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 2, color: 'var(--aj-text-mid)', cursor: 'help' }}
              >
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
                  <rect x="2" y="3" width="12" height="10" rx="1.5"/>
                  <circle cx="5.5" cy="6.5" r="1"/>
                  <path d="M3 12l3.5-3.5 2.5 2.5L11 9l2 2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {exampleImages.length > 1 && (
                  <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10 }}>{exampleImages.length}</span>
                )}
              </span>
            )}
            {isSkipped &&<span className="wf-badge wf-badge--amber" title={nrReason || undefined}>N/R{nrReason ? ': ' + nrReason : ''}</span>}
            {isDone    && <span className="wf-badge wf-badge--green">{compName || 'Done'}</span>}
          </span>
        </div>
        {/* ⋯ menu — moved to end of row when triangle takes the tri position */}
        {hasContent && !readOnly && (
          <ActionMenuButton menu={menu} />
        )}
      </div>

      {/* Expanded content: Summary + Example Images — each is its own outline line with a bullet dot */}
      {isExpanded && (
        <div className="wf-children">
          {summary && (
            <div className="wf-node wf-node--item">
              <div className="wf-row">
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                <span className="wf-bullet wf-bullet--item" />
                <div className="wf-text-wrap">
                  <span className="wf-node-text wf-node-text--item" style={{ color: 'var(--aj-text-mid)', fontWeight: 400, whiteSpace: 'normal' }}>
                    {summary}
                  </span>
                </div>
              </div>
            </div>
          )}
          {Array.isArray(exampleImages) && exampleImages.map((img, i) => {
            const src = img?.thumbnails?.large?.url || img?.thumbnails?.small?.url || img?.url
            const href = img?.url
            if (!src) return null
            return (
              <div key={i} className="wf-node wf-node--item">
                <div className="wf-row" style={{ alignItems: 'flex-start' }}>
                  <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                  <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                  <span className="wf-bullet wf-bullet--item" style={{ marginTop: 5 }} />
                  <div className="wf-text-wrap">
                    <a href={href} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>
                      <img
                        src={src}
                        alt={img?.filename || 'Example image'}
                        style={{
                          maxWidth: 220, maxHeight: 160, borderRadius: 4,
                          border: '0.5px solid var(--aj-border)',
                          objectFit: 'contain', display: 'block',
                        }}
                      />
                    </a>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* ActionMenu renders via portal — hidden in read-only (closed phase) mode */}
      {!readOnly && (
        <ActionMenu menu={menu}>
          {isSkipped ? (
            /* Not-required item: only undo action */
            <ActionMenuItem onClick={handleMarkRequired}><IcoSkip /> Mark required</ActionMenuItem>
          ) : isDone ? (
            /* Completed item: only undo action */
            <ActionMenuItem onClick={handleUncomplete}><IcoCheck /> Mark incomplete</ActionMenuItem>
          ) : (
            /* Active item: complete + not-required */
            <>
              <ActionMenuItem onClick={handleComplete}><IcoCheck /> Complete</ActionMenuItem>
              <ActionMenuDivider />
              <ActionMenuItem onClick={() => setShowNRModal(true)}><IcoSkip /> Not required</ActionMenuItem>
            </>
          )}
        </ActionMenu>
      )}
      {/* Not-required reason modal — renders via its own portal */}
      {showNRModal && (
        <SheetClNotRequiredModal
          itemName={name}
          onConfirm={reason => handleSkip(reason)}
          onCancel={() => setShowNRModal(false)}
        />
      )}
    </div>
  )
}

/* ── SheetClFloatBar — bulk action bar for checklist select mode ─────────── */
function SheetClFloatBar({ selectedIds, items, phasePrefix, currentMemberId, onDone, onCancel }) {
  const [saving, setSaving] = useState(false)
  const sel = items.filter(i => selectedIds.has(i.id))
  const fp  = fieldPfx(phasePrefix)

  async function bulkComplete() {
    if (saving) return
    setSaving(true)
    sel.forEach(item => {
      item.fields[fp + ' Complete'] = true
      if (currentMemberId) item.fields[fp + ' Completed by'] = [currentMemberId]
    })
    window.dispatchEvent(new Event('outline-render'))
    try {
      await Promise.all(sel.map(item =>
        updateSheetChecklistItem(item.id, phasePrefix, 'complete', currentMemberId)
      ))
    } catch (e) { console.error('[SheetClFloatBar] bulk complete error', e) }
    finally { setSaving(false); onDone() }
  }

  async function bulkSkip() {
    if (saving) return
    setSaving(true)
    sel.forEach(item => {
      item.fields[fp + ' Not Required'] = true
      if (currentMemberId) item.fields[fp + ' Not Required Member'] = [currentMemberId]
    })
    window.dispatchEvent(new Event('outline-render'))
    try {
      await Promise.all(sel.map(item =>
        updateSheetChecklistItem(item.id, phasePrefix, 'notRequired', currentMemberId)
      ))
    } catch (e) { console.error('[SheetClFloatBar] bulk skip error', e) }
    finally { setSaving(false); onDone() }
  }

  return (
    <div className="wf-sheet-cl-float">
      <span style={{ flex: 1 }}>{sel.length} item{sel.length !== 1 ? 's' : ''} selected</span>
      <button
        onClick={bulkComplete}
        disabled={saving || sel.length === 0}
        style={{
          background: sel.length > 0 ? '#1D9E75' : '#3a5a68', border: 'none', borderRadius: 4,
          color: '#fff', padding: '5px 14px', fontSize: 12, cursor: sel.length > 0 ? 'pointer' : 'not-allowed',
        }}
      >{saving ? '…' : 'Complete'}</button>
      <button
        onClick={bulkSkip}
        disabled={saving || sel.length === 0}
        style={{
          background: sel.length > 0 ? '#5a6a70' : '#3a5a68', border: 'none', borderRadius: 4,
          color: '#fff', padding: '5px 14px', fontSize: 12, cursor: sel.length > 0 ? 'pointer' : 'not-allowed',
        }}
      >{saving ? '…' : 'Skip'}</button>
      <button
        onClick={onCancel}
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ff9999', padding: 0, fontSize: 11 }}
      >✕ Cancel</button>
    </div>
  )
}

/* ── PDF Insert field config — editable fields per insert type ─────────────── */
const INSERT_TYPE_FIELDS = {
  'recgwsxFPA439jr8e': [  // Title Sheet
    { label: 'Allowed Lot Use',    field: 'Allowed Lot Use',    type: 'readonly_lookup' },
    { label: 'Existing Lot Use',   field: 'Existing Lot Use',   type: 'readonly_lookup' },
    {
      label: 'Proposed Lot Use', field: 'Proposed Lot Use', type: 'singleSelect',
      choices: ['Single Family Residence','Multi Family Residence','Commercial Retail',
                'Commercial Hospitality','Commercial Dining','Commercial Offices'],
    },
    {
      label: 'Deferred Approvals', field: 'Deferred Approvals', type: 'multipleSelects',
      choices: [
        'Fire Sprinkler System Per NFPA 13D Throughout Including Attics',
        'Amendment to Existing Fire Sprinkler System Per NFPA 13D',
        'Sound Attenuation Design for Exterior HVAC Equipment per AHRI Standard 275',
        'Solar Photovoltaic System',
        'Truss System',
      ],
    },
    {
      label: 'Separate Permits', field: 'Separate Permits', type: 'multipleSelects',
      choices: ['Pool and or Spa','Landscape','Property Line Fence Walls','Re-Installation of Solar Panels'],
    },
    {
      label: 'REP Scope', field: 'REP Scope', type: 'multipleSelects',
      choices: ['Driveway','Apron','Fence(s)','Gate(s)','Site wall(s)',
                'Entry walkway(s)','Stairs','Stepping stones','Planter(s)','Irrigation system'],
    },
    { label: 'Vicinity Map',  type: 'template_image', entityType: 'pdf_insert', fieldKey: 'vicinity_map' },
    { label: 'Project Image', type: 'template_image', entityType: 'project',    fieldKey: 'project_image' },
    { label: 'Open Space In Lieu', field: 'Open Space In Lieu', type: 'checkbox' },
  ],
}

/* ── PdfInsertFieldNode — one field row under the PDF Insert section ─────── */
function PdfInsertFieldNode({ label, type, value, onEdit }) {
  const menu = useActionMenu()

  const isEmpty = value === null || value === undefined || value === false ||
    value === '' || (Array.isArray(value) && !value.length)

  const displayValue = (() => {
    if (type === 'readonly_lookup') return (Array.isArray(value) ? value[0] : value) || '—'
    if (type === 'singleSelect')    return value || '—'
    if (type === 'multipleSelects') return Array.isArray(value) && value.length ? value.join(', ') : '—'
    if (type === 'checkbox')        return value ? 'Yes' : 'No'
    return '—'
  })()

  const isEditable = type !== 'readonly_lookup'

  const valStyle = {
    fontFamily: '"DM Mono",monospace', fontSize: 10, marginLeft: 8,
    color: isEmpty ? 'var(--aj-text-muted)' : 'var(--aj-text-mid)',
  }

  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-bullet wf-bullet--item" style={{ background: 'var(--wf-dot)' }} />
        <div className="wf-text-wrap">
          <span className="wf-node-text">{label}</span>
          <span style={valStyle}>{displayValue}</span>
        </div>
        {isEditable && <ActionMenuButton menu={menu} />}
      </div>

      {isEditable && (
        <ActionMenu menu={menu}>
          <ActionMenuItem onClick={() => { menu.close(); onEdit() }}>Edit</ActionMenuItem>
        </ActionMenu>
      )}
    </div>
  )
}

/* ── TemplateImageFieldRow — Supabase-backed image field (Vicinity Map, Project Image) ─────
   entityAirtableId is the PDF Insert record ID for entityType='pdf_insert', or the Project
   record ID for entityType='project'. Airtable is never read here — Supabase is the source of
   truth; api/template-images.js best-effort mirrors uploads back into the Airtable attachment
   field so the Documint template (which reads the Airtable record directly) stays populated. */
function TemplateImageFieldRow({ label, entityType, entityAirtableId, fieldKey, projectId }) {
  const [image, setImage]         = useState(undefined)   // undefined = loading, null = none, object = loaded
  const [busyAction, setBusyAction] = useState(null)       // null | 'upload' | 'generate'
  const busy = busyAction !== null
  const fileInputRef = useRef(null)
  const loadedRef     = useRef(false)
  const menu = useActionMenu()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (loadedRef.current || !entityAirtableId) return
    loadedRef.current = true
    getTemplateImage(entityType, entityAirtableId, fieldKey)
      .then(img => setImage(img || null))
      .catch(() => setImage(null))
  }, [entityType, entityAirtableId, fieldKey])

  async function handleFile(e) {
    const file = e.target.files && e.target.files[0]
    e.target.value = ''
    if (!file || busy) return
    setBusyAction('upload')
    try {
      setImage(await uploadTemplateImage(entityType, entityAirtableId, fieldKey, file))
    } catch (err) {
      alertDialog({ title: 'Upload failed', message: err.message || String(err) })
    } finally {
      setBusyAction(null)
    }
  }

  // Vicinity Map only — geocodes the site address and renders a clean styled map with a pin,
  // replacing the manual "screenshot Google Maps" workflow. Generated image goes through the
  // same upload pipeline as a manual file, so the Airtable mirror still applies.
  async function handleGenerateFromAddress() {
    if (busy) return
    menu.close()
    setBusyAction('generate')
    let defaultAddress = ''
    try { defaultAddress = await getProjectAddressForVicinityMap(projectId) } catch { /* fall back to blank */ }
    setBusyAction(null)

    const address = await promptDialog({
      title: 'Generate vicinity map', message: 'Confirm or edit the site address:',
      defaultValue: defaultAddress, placeholder: '123 Main St, Laguna Beach, CA 92651',
    })
    if (address === null) return
    if (!address.trim()) { alertDialog({ title: 'Address required', message: 'Enter an address to generate a map.' }); return }

    setBusyAction('generate')
    try {
      const blob = await generateVicinityMapImage(address.trim())
      const file = new File([blob], 'vicinity-map.png', { type: 'image/png' })
      setImage(await uploadTemplateImage(entityType, entityAirtableId, fieldKey, file))
    } catch (err) {
      alertDialog({ title: 'Generate failed', message: err.message || String(err) })
    } finally {
      setBusyAction(null)
    }
  }

  const isEmpty = image === null || image === undefined
  const valStyle = {
    fontFamily: '"DM Mono",monospace', fontSize: 10, marginLeft: 8,
    color: isEmpty ? 'var(--aj-text-muted)' : 'var(--aj-text-mid)',
  }

  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-bullet wf-bullet--item" style={{ background: 'var(--wf-dot)' }} />
        <div className="wf-text-wrap">
          <span className="wf-node-text">{label}</span>
          <span style={valStyle}>{image === undefined ? 'Loading…' : (image ? (image.filename || '1 file') : '—')}</span>
          {image?.url && (
            <a href={image.url} target="_blank" rel="noopener noreferrer"
               style={{ marginLeft: 6, fontSize: 10, color: 'var(--aj-text)', opacity: 0.5 }}
               onClick={e => e.stopPropagation()}>↗</a>
          )}
          {busy && <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)', marginLeft: 8 }}>{busyAction === 'generate' ? 'Generating…' : 'Uploading…'}</span>}
        </div>
        {entityAirtableId && <ActionMenuButton menu={menu} />}
      </div>

      {entityAirtableId && (
        <ActionMenu menu={menu}>
          {fieldKey === 'vicinity_map' && projectId && (
            <ActionMenuItem onClick={handleGenerateFromAddress}>Generate from address</ActionMenuItem>
          )}
          <ActionMenuItem onClick={() => { menu.close(); fileInputRef.current?.click() }}>
            {image ? 'Replace image' : 'Upload image'}
          </ActionMenuItem>
        </ActionMenu>
      )}
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
    </div>
  )
}

/* ── PdfInsertEditModal — full-form modal for all PDF Insert fields ────────── */
function PdfInsertEditModal({ insertRecord, insertTypeName, onSave, onClose }) {
  // template_image fields (Vicinity Map, Project Image) have their own inline upload row — see
  // TemplateImageFieldRow — and never appear in this modal.
  const typeFields = (INSERT_TYPE_FIELDS[insertTypeName] || []).filter(f => f.type !== 'template_image')
  const [draft, setDraft] = useState(() => {
    const d = {}
    typeFields.forEach(f => { d[f.field] = insertRecord.fields[f.field] })
    return d
  })
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    setSaving(true)
    try {
      const updates = {}
      typeFields.forEach(f => {
        if (f.type !== 'readonly_lookup') updates[f.field] = draft[f.field] ?? null
      })
      await onSave(updates)
      onClose()
    } catch {
      setSaving(false)
    }
  }

  function set(field, value) {
    setDraft(prev => ({ ...prev, [field]: value }))
  }

  function toggleMulti(field, choice) {
    const current = Array.isArray(draft[field]) ? draft[field] : []
    set(field, current.includes(choice) ? current.filter(v => v !== choice) : [...current, choice])
  }

  const inp = { fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '6px 8px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)', outline: 'none', width: '100%', boxSizing: 'border-box' }

  return createPortal(
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, padding: '28px 32px', width: 500, maxWidth: '90vw', maxHeight: '82vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 20 }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--aj-text)' }}>
          Edit PDF Insert
        </div>

        {typeFields.map(f => (
          <div key={f.field} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, fontWeight: 600, color: 'var(--aj-text-mid)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {f.label}
            </div>

            {f.type === 'readonly_lookup' && (
              <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 12, color: 'var(--aj-text)' }}>
                {(Array.isArray(draft[f.field]) ? draft[f.field][0] : draft[f.field]) || '—'}
              </span>
            )}

            {f.type === 'singleSelect' && (
              <select value={draft[f.field] || ''} onChange={e => set(f.field, e.target.value || null)} style={inp}>
                <option value="">— None —</option>
                {f.choices.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}

            {f.type === 'multipleSelects' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {f.choices.map(c => {
                  const selected = Array.isArray(draft[f.field]) && draft[f.field].includes(c)
                  return (
                    <label key={c} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', lineHeight: '1.4' }}>
                      <input type="checkbox" checked={selected} onChange={() => toggleMulti(f.field, c)} style={{ marginTop: 2, accentColor: '#07272D', flexShrink: 0 }} />
                      {c}
                    </label>
                  )
                })}
              </div>
            )}

            {f.type === 'checkbox' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)' }}>
                <input type="checkbox" checked={!!draft[f.field]} onChange={e => set(f.field, e.target.checked)} style={{ accentColor: '#07272D' }} />
                {draft[f.field] ? 'Yes' : 'No'}
              </label>
            )}

          </div>
        ))}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 8, borderTop: '0.5px solid var(--aj-border)', marginTop: 4 }}>
          <button onClick={onClose} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 16px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'transparent', color: 'var(--aj-text)', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 16px', border: 'none', borderRadius: 4, background: '#07272D', color: '#fff', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

/* ── PdfInsertSection — collapsible PDF Insert node under a SheetRow ─────── */
// Shown only when the sheet's template has an associated PDF Insert Type
// (sheet.insertTypeName is non-empty). One insert record per sheet; never
// allows creation of a second or deletion of an existing one.
function PdfInsertSection({ sheetId, insertTypeName, projectId: propProjectId }) {
  const projectId   = propProjectId || ctx.recordId || wf.zoomProjectId || null
  const colKey      = 'sheet:' + sheetId + ':pdfinsert'
  const isCollapsed = wf.collapsed[colKey] !== false
  const loadedRef   = useRef(false)
  // undefined = not started, null = loading, false = no insert, object = insert record
  const [status, setStatus]         = useState(undefined)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const menu = useActionMenu()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (loadedRef.current) return
    loadedRef.current = true
    setStatus(null)
    getSheetPdfInsert(sheetId)
      .then(record => {
        setStatus(record || false)
        // Self-heal: older inserts were created without a Project Concept, and a
        // re-selected concept should carry through to the next generation.
        if (record) syncPdfInsertConcept(record, projectId)
          .then(cid => { if (cid) setStatus(prev => prev && prev.id === record.id ? { ...prev, fields: { ...prev.fields, 'Project Concept': [cid] } } : prev) })
          .catch(err => console.warn('[PdfInsertSection] concept sync failed', err))
      })
      .catch(() => { loadedRef.current = false; setStatus(undefined) })
  }, [sheetId])

  async function handleCreate() {
    menu.close()
    try {
      const types = await getPdfInsertTypes()
      const match = types.find(t => t.id === insertTypeName)
      if (!match) {
        alertDialog({ title: 'Type not found', message: 'Could not find PDF Insert Type "' + insertTypeName + '".' })
        return
      }
      setStatus(null)
      const record = await createSheetPdfInsert(sheetId, match.id, projectId)
      if (record && record.id) { setStatus(record); window.dispatchEvent(new Event('outline-render')) }
      else setStatus(false)
    } catch (err) {
      console.error('[PdfInsertSection] create error', err)
      setStatus(false)
    }
  }

  async function handleSaveInsert(updates) {
    if (!status || status === false) return
    setStatus(prev => ({ ...prev, fields: { ...prev.fields, ...updates } }))
    try {
      await updateSheetPdfInsert(status.id, updates)
    } catch (err) {
      console.error('[PdfInsertSection] field update error', err)
      loadedRef.current = false
      setStatus(undefined)
      throw err
    }
  }

  const hasInsert   = status && status !== false
  const attachments = hasInsert ? (status.fields['Generated Insert'] || []) : []
  const isGenerated = attachments.length > 0
  const generateUrl = hasInsert ? (status.fields['Generate PDF Insert'] || '') : ''
  const pdfUrl      = isGenerated ? (attachments[0]?.url || '') : ''

  const missingConcept = hasInsert && !(status.fields['Project Concept'] || []).length
  const statusMeta = hasInsert ? (
    <span style={{
      fontFamily: '"DM Mono",monospace', fontSize: 10, marginLeft: 6,
      color: isGenerated ? '#1D9E75' : 'var(--aj-text-mid)',
    }}>
      {isGenerated ? '✓ Generated' : '○ Not generated'}
      {missingConcept && (
        <span style={{ color: '#B7791F', marginLeft: 8 }} title="Mark a concept as Selected in Reference › Design › Concepts so the insert can pull project data">
          no selected concept
        </span>
      )}
    </span>
  ) : null

  const typeFields = INSERT_TYPE_FIELDS[insertTypeName] || []

  return (
    <div className="wf-node wf-node--section">
      <div className="wf-row" onClick={e => e.stopPropagation()}>
        <button
          className="wf-triangle"
          onClick={e => {
            e.stopPropagation()
            wf.collapsed[colKey] = !isCollapsed
            window.dispatchEvent(new Event('outline-render'))
          }}
        >
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`} />
        </button>
        <button className="wf-bullet wf-bullet--section" style={{ cursor: 'default' }} />
        <div className="wf-text-wrap">
          <span className="wf-node-text">PDF Insert</span>
          {status !== undefined && status !== null && statusMeta}
        </div>
        {status !== undefined && status !== null && (
          <ActionMenuButton menu={menu} />
        )}
      </div>

      <ActionMenu menu={menu}>
        {status === false ? (
          <ActionMenuItem onClick={handleCreate}>Create PDF Insert</ActionMenuItem>
        ) : hasInsert ? (
          <>
            {generateUrl && (
              <ActionMenuItem onClick={() => { menu.close(); window.open(generateUrl, '_blank', 'noopener,noreferrer') }}>
                Generate PDF Insert ↗
              </ActionMenuItem>
            )}
            {isGenerated && pdfUrl && (
              <ActionMenuItem onClick={() => { menu.close(); window.open(pdfUrl, '_blank', 'noopener,noreferrer') }}>
                View PDF ↗
              </ActionMenuItem>
            )}
          </>
        ) : null}
      </ActionMenu>

      {!isCollapsed && (
        <div className="wf-children">
          {(status === undefined || status === null) && (
            <div className="wf-node wf-node--item">
              <div className="wf-row">
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
                <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }} />
                <div className="wf-text-wrap">
                  <span className="wf-node-text" style={{ color: 'var(--aj-text-muted)' }}>Loading…</span>
                </div>
              </div>
            </div>
          )}

          {status === false && (
            <div className="wf-node wf-node--item">
              <div className="wf-row">
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
                <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }} />
                <div className="wf-text-wrap">
                  <span className="wf-node-text" style={{ color: 'var(--aj-text-mid)', fontStyle: 'italic' }}>
                    No insert created yet
                  </span>
                </div>
              </div>
            </div>
          )}

          {hasInsert && typeFields.map(f => (
            f.type === 'template_image' ? (
              <TemplateImageFieldRow
                key={f.fieldKey}
                label={f.label}
                entityType={f.entityType}
                fieldKey={f.fieldKey}
                entityAirtableId={f.entityType === 'project' ? projectId : status.id}
                projectId={projectId}
              />
            ) : (
              <PdfInsertFieldNode
                key={f.field}
                label={f.label}
                type={f.type}
                value={status.fields[f.field]}
                onEdit={() => setEditModalOpen(true)}
              />
            )
          ))}
        </div>
      )}

      {editModalOpen && hasInsert && (
        <PdfInsertEditModal
          insertRecord={status}
          insertTypeName={insertTypeName}
          onSave={handleSaveInsert}
          onClose={() => setEditModalOpen(false)}
        />
      )}
    </div>
  )
}

/* ── SheetRow — individual expandable sheet row ──────────────────────────── */

/*
 * Sheet prop shape:
 *   id:             string  — Drawing Sheet record ID
 *   title:          string  — combined "Sheet Number and Title" (display fallback)
 *   sheetNumber:    string  — editable number
 *   sheetTitle:     string  — editable title
 *   progress:       string  — progress label
 *   insertTypeName: string  — PDF Insert Type name from template; non-empty = shows PDF Insert section
 *   revision:       object|null — full Airtable revision record (milestone context only)
 *   revisionId:     string|null — revision record ID (milestone context only)
 */

/* ── EditSheetModal — modal for changing sheet number, title, and template ─── */
function EditSheetModal({ sheetNumber, sheetTitle, projectId, currentSheetId, onSave, onCancel }) {
  const [num,    setNum]    = useState(sheetNumber || '')
  const [title,  setTitle]  = useState(sheetTitle  || '')
  const [saving, setSaving] = useState(false)
  // Project-wide sheet numbers (excluding this sheet) for duplicate validation.
  const [takenNumbers,      setTakenNumbers]      = useState(null)  // null = loading
  // Template state — null = still loading (blocks save to prevent accidental template clear)
  const [originalTemplateId, setOriginalTemplateId] = useState(null)
  const [templateId,          setTemplateId]          = useState('')
  const [templates,           setTemplates]           = useState(null)  // null = loading

  useEffect(() => {
    let cancelled = false
    if (!projectId) {
      setTakenNumbers(new Set())
      setOriginalTemplateId('')
      return
    }
    // Load project sheets: duplicate-number validation + current sheet's template
    getProjectSheets(projectId).then(sheets => {
      if (cancelled) return
      setTakenNumbers(new Set((sheets || [])
        .filter(s => s.id !== currentSheetId)
        .map(s => String(s.fields['Sheet Number'] || '').trim().toLowerCase())
        .filter(Boolean)))
      const thisSheet = (sheets || []).find(s => s.id === currentSheetId)
      const tplId = (thisSheet?.fields?.['Drawing Sheet Template'] || [])[0] || ''
      setOriginalTemplateId(tplId)
      setTemplateId(tplId)
    }).catch(() => { if (!cancelled) { setTakenNumbers(new Set()); setOriginalTemplateId('') } })
    // Load available templates (independent of project)
    getSheetTemplates().then(tpls => { if (!cancelled) setTemplates(tpls || []) })
                       .catch(() => { if (!cancelled) setTemplates([]) })
    return () => { cancelled = true }
  }, [projectId, currentSheetId]) // eslint-disable-line react-hooks/exhaustive-deps

  const normNum = num.trim().toLowerCase()
  const isDup = !!normNum && !!takenNumbers && takenNumbers.has(normNum)
  // Block save until the original template has been resolved (prevents accidental template clear)
  const templateLoaded = originalTemplateId !== null
  const templateChanged = templateLoaded && templateId !== originalTemplateId
  const canSave = !!num.trim() && !isDup && !saving && templateLoaded

  async function handleSave() {
    if (!canSave) return
    // If there was an existing template and it changed, confirm the checklist reset
    if (templateChanged && !!originalTemplateId) {
      const ok = await confirmDialog({
        title: 'Reset checklist?',
        message: 'Changing the sheet template will reset this sheet\'s checklist. ' +
          'Completed items and "Not Required" marks will be preserved, but unmatched items will be removed.',
        confirmLabel: 'Change template',
        danger: true,
      })
      if (!ok) return
    }
    setSaving(true)
    await onSave(num.trim(), title.trim(), templateId, templateChanged)
  }

  // Group templates by discipline for <optgroup> display
  const tplByDisc = {}
  for (const t of (templates || [])) {
    const disc = (t.fields?.['Discipline'] || 'Other').trim() || 'Other'
    if (!tplByDisc[disc]) tplByDisc[disc] = []
    tplByDisc[disc].push(t)
  }
  const sortedDiscs = Object.keys(tplByDisc).sort((a, b) => {
    const ai = DISCIPLINE_ORDER.indexOf(a), bi = DISCIPLINE_ORDER.indexOf(b)
    return (ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi)
  })

  const inputStyle = {
    display: 'block', width: '100%', boxSizing: 'border-box',
    fontFamily: '"DM Sans",sans-serif', fontSize: 13, padding: '7px 10px',
    borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', outline: 'none',
    color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)',
  }
  const labelStyle = {
    display: 'block', fontFamily: '"DM Sans",sans-serif', fontSize: 11,
    fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase',
    color: 'var(--aj-text-muted)', marginBottom: 5,
  }

  return createPortal(
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
        zIndex: 9001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, border: '0.5px solid var(--aj-border)',
        padding: '24px 24px 20px', width: 420, maxWidth: '100%',
        boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
        <div style={{ fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 12,
          letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)', marginBottom: 20 }}>
          Edit Drawing Sheet
        </div>
        <label style={labelStyle}>Sheet Number</label>
        <input
          autoFocus
          value={num}
          onChange={e => setNum(e.target.value)}
          placeholder="e.g. A1.1"
          onKeyDown={e => { if (e.key === 'Escape') onCancel() }}
          style={{ ...inputStyle, marginBottom: isDup ? 4 : 14,
            fontFamily: '"DM Mono",monospace', fontSize: 12,
            ...(isDup ? { borderColor: '#dc2626', background: 'rgba(192,57,43,0.14)' } : {}) }}
        />
        {isDup && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#dc2626', marginBottom: 12 }}>That sheet number already exists in this project.</div>}
        <label style={labelStyle}>Sheet Title</label>
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="e.g. Floor Plan"
          onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') onCancel() }}
          style={{ ...inputStyle, marginBottom: 14 }}
        />
        <label style={labelStyle}>Sheet Template</label>
        {templates === null ? (
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)', marginBottom: templateChanged ? 6 : 20 }}>Loading templates…</div>
        ) : (
          <select
            value={templateId}
            onChange={e => setTemplateId(e.target.value)}
            style={{ ...inputStyle, marginBottom: templateChanged ? 6 : 20, cursor: 'pointer', appearance: 'auto' }}
          >
            <option value="">— No template —</option>
            {sortedDiscs.map(disc => (
              <optgroup key={disc} label={disc}>
                {tplByDisc[disc].map(t => (
                  <option key={t.id} value={t.id}>
                    {t.fields['Sheet Number']} — {t.fields['Sheet Name']}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        )}
        {templateChanged && (
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#d97706',
            background: 'rgba(183,101,26,0.14)', border: '0.5px solid #d97706', borderRadius: 4,
            padding: '7px 10px', marginBottom: 20 }}>
            {originalTemplateId
              ? 'Changing the template will reset this sheet\'s checklist — you\'ll confirm before saving.'
              : 'A checklist will be created from this template when saved.'}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13,
            padding: '7px 18px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)',
            background: 'transparent', cursor: 'pointer', color: 'var(--aj-text-mid)' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={!canSave}
            style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, padding: '7px 18px',
              borderRadius: 4, border: 'none', background: '#07272D',
              color: '#fff', cursor: !canSave ? 'not-allowed' : 'pointer', opacity: !canSave ? 0.5 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// Who's drafting this sheet — visibility only, not a RASCI revival and not a gate (see
// docs/QC_WORKFLOW.md). One assignee per sheet revision; picking a new one replaces it.
function AssignDrafterModal({ assignedMemberId, assignedMemberName, onSave, onClear, onCancel }) {
  const [members, setMembers] = useState(null)  // null = loading
  const [pickedId, setPickedId] = useState(assignedMemberId || '')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    getTeamMembers().then(m => { if (!cancelled) setMembers(m || []) }).catch(() => { if (!cancelled) setMembers([]) })
    return () => { cancelled = true }
  }, [])

  const items = (members || []).map(m => ({ id: m.id, label: m.name }))
  const canSave = !!pickedId && !saving

  async function handleSave() {
    if (!canSave) return
    const m = (members || []).find(x => x.id === pickedId)
    if (!m) return
    setSaving(true)
    await onSave(m)
  }
  async function handleClear() {
    setSaving(true)
    await onClear()
  }

  const labelStyle = {
    display: 'block', fontFamily: '"DM Sans",sans-serif', fontSize: 11,
    fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase',
    color: 'var(--aj-text-muted)', marginBottom: 5,
  }
  const inputStyle = {
    display: 'block', width: '100%', boxSizing: 'border-box',
    fontFamily: '"DM Sans",sans-serif', fontSize: 13, padding: '7px 10px',
    borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', outline: 'none',
    color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)',
  }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
      zIndex: 9001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, border: '0.5px solid var(--aj-border)',
        padding: '24px 24px 20px', width: 380, maxWidth: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
        <div style={{ fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 12,
          letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)', marginBottom: 20 }}>
          {assignedMemberName ? 'Reassign Drafter' : 'Assign Drafter'}
        </div>
        <label style={labelStyle}>Who's drafting this sheet?</label>
        <SearchablePicker items={items} value={pickedId} onChange={setPickedId} placeholder="Search team…" inputStyle={{ ...inputStyle, marginBottom: 20 }} />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
          {assignedMemberName ? (
            <button onClick={handleClear} disabled={saving} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13,
              padding: '7px 12px', borderRadius: 4, border: 'none', background: 'transparent',
              cursor: saving ? 'not-allowed' : 'pointer', color: '#c0392b' }}>
              Clear assignment
            </button>
          ) : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onCancel} disabled={saving} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13,
              padding: '7px 18px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)',
              background: 'transparent', cursor: 'pointer', color: 'var(--aj-text-mid)' }}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={!canSave} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13,
              padding: '7px 18px', borderRadius: 4, border: 'none', background: '#07272D',
              color: '#fff', cursor: !canSave ? 'not-allowed' : 'pointer', opacity: !canSave ? 0.5 : 1 }}>
              {saving ? 'Saving…' : 'Assign'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export function SheetRow({ sheet, context, milestonePhasePrefix, currentMemberId, milestoneId = null, readOnly = false, selectMode, isSelected, onToggleSelect, projectId, projectName, onSheetZoom = null, zoomedAs = null }) {
  const sheetId   = sheet.id
  const isExpanded = wf.collapsed['sheet:' + sheetId] === false  // undefined/true = collapsed

  const revLoadedRef = useRef(false)

  // Manager-only: mark a legacy/pre-QC drawing "approved" without running it through QC.
  // Never offered once a real QC chain exists (see the menu-item gate below) — the server
  // enforces this too. See docs/QC_WORKFLOW.md.
  const [overridingQc, setOverridingQc] = useState(false)
  async function handleOverrideApprove() {
    if (overridingQc) return
    const ok = await confirmDialog({
      title: 'Mark as approved without QC?',
      message: 'This sheet never went through the in-app QC process. Marking it approved treats it as done for this milestone — it will show "✓ Approved (Override)" instead of a real QC history.',
      confirmLabel: 'Mark approved',
    })
    if (!ok) return
    const reason = await promptDialog({ title: 'Reason (optional)', message: 'Why is this being marked approved without QC?', placeholder: 'e.g. legacy drawing, imported before the QC tool existed' })
    if (reason === null) return
    setOverridingQc(true)
    try {
      await overrideApproveRevision(sheetId, sheet.revisionId, reason || undefined)
      if (ctx.qcSummaryCache) ctx.qcSummaryCache[sheet.revisionId] = undefined
      window.dispatchEvent(new CustomEvent('qc-updated', { detail: { sheetId } }))
      window.dispatchEvent(new Event('outline-render'))
    } catch (e) {
      alertDialog({ title: 'Could not mark approved', message: e.message || 'Something went wrong.' })
    } finally {
      setOverridingQc(false)
    }
  }

  // Local overrides for number/title (survive re-renders without a full reload)
  const [localNumber, setLocalNumber] = useState(null)
  const [localTitle,  setLocalTitle]  = useState(null)

  // Edit sheet modal
  const [showEditModal, setShowEditModal] = useState(false)

  // Draft assignment — visibility only (docs/QC_WORKFLOW.md), scoped to this sheet revision.
  const [showAssignModal, setShowAssignModal] = useState(false)

  // Slack discussion state
  const [showSlackPanel,  setShowSlackPanel]  = useState(false)
  const [localThreadTs,   setLocalThreadTs]   = useState(sheet.slackThreadTs || '')

  // Action menus
  const rowMenu       = useActionMenu()
  const clMenu        = useActionMenu()
  const [clSelectMode,  setClSelectMode]  = useState(false)
  const [selectedClIds, setSelectedClIds] = useState(() => new Set())

  // QC auto-detection: prompt shown when all checklist items complete/skipped
  const [showReadyForQCPrompt, setShowReadyForQCPrompt] = useState(false)
  // AI QC panel
  // Remove sheet from milestone (delete revision, optionally delete Drawing Sheet)
  const [showRemoveModal, setShowRemoveModal] = useState(false)
  // Whether THIS revision is the sheet's only version (true → removing it deletes the whole
  // sheet). Resolved when the ⋯ menu OPENS — one cheap fetch at the moment it's relevant, not
  // per row render — so the Remove tooltip states the real outcome instead of hedging.
  // null = not yet known (generic tooltip).
  const [soleVersion, setSoleVersion] = useState(null)
  useEffect(() => {
    if (!rowMenu.isOpen || context !== 'milestone' || !sheet.revisionId) return
    let cancelled = false
    getSheetRevisions(sheetId)
      .then(revs => { if (!cancelled) setSoleVersion((revs || []).length === 1 && sheet.revision?.fields?.['Type'] === 'Original Release') })
      .catch(() => {})
    return () => { cancelled = true }
  }, [rowMenu.isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Lazy-load: production revisions ──────────────────────────────────────
  function maybeLoadRevisions() {
    if (revLoadedRef.current) return
    if (!ctx.sheetRevCache || ctx.sheetRevCache[sheetId] !== undefined) {
      revLoadedRef.current = true; return
    }
    revLoadedRef.current = true
    ctx.sheetRevCache[sheetId] = null  // in-flight
    getSheetRevisions(sheetId)
      .then(records => {
        if (ctx.sheetRevCache) ctx.sheetRevCache[sheetId] = records
        window.dispatchEvent(new Event('outline-render'))
      })
      .catch(() => {
        if (ctx.sheetRevCache) ctx.sheetRevCache[sheetId] = undefined  // reset to retry
        window.dispatchEvent(new Event('outline-render'))
      })
  }

  // ── Lazy-load: sheet checklist (milestone context) ────────────────────────
  // DataLoader deduplicates — if MilestoneSections already bulk-loaded (null in-flight or array),
  // ctx.loader.load sees a non-undefined value and returns immediately. No clLoadedRef needed.
  function maybeLoadChecklist() {
    ctx.loader.load('sheetCl:' + sheetId, () => getSheetChecklistItems(sheetId))
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isExpanded && !zoomedAs) return
    if (context === 'production') maybeLoadRevisions()
    if (context === 'milestone') maybeLoadChecklist()
  }, [isExpanded, zoomedAs]) // eslint-disable-line react-hooks/exhaustive-deps

  // Force-refetch this sheet's revisions when a milestone touching it gets posted (dispatched by
  // PostMilestoneModal.jsx) — without this, Reference/Production > Drawing Sheets kept showing
  // pre-post data (Posted Date, revision Type, issued/stamped PDF) until a hard page reload, since
  // ctx.sheetRevCache is a lazy-load-once cache with no other invalidation path (Matt, 2026-07-23).
  // Fires regardless of expanded state — the COLLAPSED row's posted/in-progress summary badge
  // reads this same cache live too (see prodPostedRevisions/prodInProgressRevisions above).
  useEffect(() => {
    if (context !== 'production') return
    function onStale(e) {
      const ids = e.detail?.sheetIds
      if (Array.isArray(ids) && !ids.includes(sheetId)) return
      revLoadedRef.current = false
      if (ctx.sheetRevCache) ctx.sheetRevCache[sheetId] = undefined
      maybeLoadRevisions()
    }
    window.addEventListener('sheet-revisions-stale', onStale)
    return () => window.removeEventListener('sheet-revisions-stale', onStale)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Checklist actions ─────────────────────────────────────────────────────
  async function handleResetChecklist() {
    // Reset both DataLoader and legacy cache — show loading indicator
    ctx.loader.reset('sheetCl:' + sheetId)
    if (ctx.sheetClCache) ctx.sheetClCache[sheetId] = null
    window.dispatchEvent(new Event('outline-render'))
    try {
      await resetSheetChecklist(sheetId)
    } catch (err) {
      console.error('[DrawingSheetList] Reset sheet checklist error', err)
    }
    // DataLoader was reset above; maybeLoadChecklist will re-fetch via ctx.loader.load
    maybeLoadChecklist()
  }

  // ── QC auto-detection ─────────────────────────────────────────────────────
  // Called after each checklist item is completed or skipped.
  // Checks if all phase items are now done; if so, prompts to mark Ready for QC.
  function checkAllComplete() {
    if (!sheet.revisionId || !milestonePhasePrefix) return
    const f = sheet.revision?.fields || {}
    if (f['Ready for QC'] || f['QC Approved']) return  // already at QC state or beyond
    const cached = ctx.loader.get('sheetCl:' + sheetId)
    if (!Array.isArray(cached) || !cached.length) return
    const fp2 = fieldPfx(milestonePhasePrefix)
    const inPhase = i => {
      const dr = i.fields['Drawing Release (from Deliverable Checklist Item Template)']
      return Array.isArray(dr) ? dr.includes(milestonePhasePrefix) : dr === milestonePhasePrefix
    }
    const filtered = cached.filter(inPhase)
    if (!filtered.length) return
    const allDone = filtered.every(i => i.fields[fp2 + ' Complete'] || i.fields[fp2 + ' Not Required'])
    if (allDone) setShowReadyForQCPrompt(true)
  }

  // ── Data ──────────────────────────────────────────────────────────────────
  const prodRevisions = context === 'production' ? ctx.sheetRevCache?.[sheetId] : null
  // Filter out revisions linked to unposted milestones (no Posted Date); milestone-branch
  // still shows all revisions regardless since that's where work on them occurs.
  const prodPostedRevisions = Array.isArray(prodRevisions)
    ? prodRevisions.filter(rev => getFirst(rev.fields['Posted Date (from Deliverables)']))
    : prodRevisions
  // The revision(s) currently being built out (no Posted Date yet) — so a sheet with zero
  // posted revisions still shows WHICH milestone(s) are working on it, instead of looking idle.
  // ALL matches, not just the first — a sheet carried forward into more than one still-open
  // milestone at once (e.g. an actively-worked revision plus a freshly-scaffolded later phase)
  // used to have every match past the first silently vanish from Production entirely; there's
  // still no explicit sequence field on Sheet Revision to order them by (see
  // docs/QC_WORKFLOW.md), so they render in whatever order prodRevisions arrived in.
  const prodInProgressRevisions = Array.isArray(prodRevisions)
    ? prodRevisions.filter(rev => !getFirst(rev.fields['Posted Date (from Deliverables)']))
    : []

  const msRevision = context === 'milestone' ? (sheet.revision || null) : null

  const clRawItems = (context === 'milestone' && milestonePhasePrefix)
    ? ctx.loader.get('sheetCl:' + sheetId)
    : null

  const clFiltered = (Array.isArray(clRawItems) && milestonePhasePrefix)
    ? clRawItems
        .filter(item => {
          // 'Drawing Release (from Deliverable Checklist Item Template)' is a multipleLookupValues
          // field that returns the Drawing Releases multiselect values from the linked template.
          // Values match the template's multiselect options: 'DAPC', 'PPC / BID', 'EXT', 'INT'.
          const dr = item.fields['Drawing Release (from Deliverable Checklist Item Template)']
          return Array.isArray(dr) ? dr.includes(milestonePhasePrefix) : dr === milestonePhasePrefix
        })
        .sort((a, b) => {
          const sa = Number(getFirst(a.fields['Sort Order (from Deliverable Checklist Item Template)'])) || 999
          const sb = Number(getFirst(b.fields['Sort Order (from Deliverable Checklist Item Template)'])) || 999
          return sa - sb
        })
    : null

  // PDF icon URL for sheet row
  let rowPdfUrl = null
  if (context === 'milestone' && msRevision) {
    rowPdfUrl = getPdfUrl(msRevision)
  } else if (context === 'production' && Array.isArray(prodRevisions) && prodRevisions.length) {
    rowPdfUrl = getPdfUrl(prodRevisions[0])
  }

  // ── Display values ────────────────────────────────────────────────────────
  const numDisplay   = localNumber  ?? sheet.sheetNumber  ?? ''
  const titleDisplay = localTitle   ?? sheet.sheetTitle   ?? ''
  const fallback     = sheet.title  || 'Untitled'
  const color        = sheet.progress ? sheetProgColor(sheet.progress) : null

  // ── QC prompt element — computed once, included in each render path ──────
  // Shown after the last checklist item is completed/skipped (auto-detected).
  const qcPromptEl = showReadyForQCPrompt && !readOnly && sheet.revisionId && context === 'milestone' ? (
    <div className="wf-qc-prompt">
      <span>All checklist items complete — mark as Ready for QC?</span>
      <button onClick={() => {
        setShowReadyForQCPrompt(false)
        if (sheet.revision) sheet.revision.fields['Ready for QC'] = true
        window.dispatchEvent(new Event('outline-render'))
        updateSheetRevisionQC(sheet.revisionId, { 'Ready for QC': true })
          .catch(e => console.error('[SheetRow] QC ready error', e))
      }}>Yes</button>
      <button onClick={() => setShowReadyForQCPrompt(false)}>Not yet</button>
    </div>
  ) : null

  // ── Section zoom render paths ─────────────────────────────────────────────
  // Section zoom render rule: when zoomedAs is set, render content only — no header row.
  // ZoomTitle already shows the sheet name in the breadcrumb/title bar.

  if (zoomedAs === 'cl') {
    // Checklist items only — no section header
    const loading = clRawItems === undefined || clRawItems === null
    const empty   = Array.isArray(clRawItems) && Array.isArray(clFiltered) && clFiltered.length === 0
    return (
      <>
        {loading && (
          <div className="wf-node wf-node--item">
            <div className="wf-row">
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
              <div className="wf-text-wrap"><span className="wf-node-text" style={{ color: 'var(--aj-text-muted)' }}>Loading…</span></div>
            </div>
          </div>
        )}
        {empty && (
          <div className="wf-node wf-node--item">
            <div className="wf-row">
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
              <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
              <div className="wf-text-wrap"><span className="wf-node-text" style={{ color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>No checklist items for this phase.</span></div>
            </div>
          </div>
        )}
        {clFiltered && clFiltered.map(item => (
          <SheetClItemNode
            key={item.id}
            item={item}
            sheetId={sheetId}
            phasePrefix={milestonePhasePrefix}
            currentMemberId={currentMemberId}
            readOnly={readOnly}
            selectMode={clSelectMode}
            isSelected={selectedClIds.has(item.id)}
            onToggleSelect={id => setSelectedClIds(prev => {
              const next = new Set(prev)
              next.has(id) ? next.delete(id) : next.add(id)
              return next
            })}
            onItemCompleted={checkAllComplete}
          />
        ))}
        {qcPromptEl}
      </>
    )
  }

  if (zoomedAs === 'sheet') {
    // Sheet content only — no wf-row header (section zoom render rule)
    return (
      <>
        {/* Production: all revisions, most recent first */}
        {context === 'production' && (
          <>
            {(prodRevisions === undefined || prodRevisions === null) && (
              <div className="wf-loading" style={{ padding: '5px 8px 5px 49px', fontSize: 12 }}>Loading revisions...</div>
            )}
            {Array.isArray(prodRevisions) && prodRevisions.length === 0 && (
              <div className="wf-loading" style={{ padding: '5px 8px 5px 49px', fontSize: 12, color: 'var(--aj-text-muted)' }}>No revisions yet</div>
            )}
            {Array.isArray(prodRevisions) && prodRevisions.map((rev, i) => (
              <RevisionRow key={rev.id} revision={rev} isLatest={i === 0} showMilestone={true} revisionId={rev.id} />
            ))}
          </>
        )}
        {/* Milestone: single revision + checklist section */}
        {context === 'milestone' && (
          <>
            {msRevision && (
              // milestonePhasePrefix MUST reach RevisionRow — its PDF-upload checklist gate is
              // silently skipped without it (the other milestone call site below passes it too).
              <RevisionRow key={msRevision.id} revision={msRevision} isLatest={false} showMilestone={false} revisionId={msRevision.id} milestonePhasePrefix={milestonePhasePrefix} canManagePdf />
            )}
            {milestonePhasePrefix && (() => {
              const clCollapseKey  = 'sheet:' + sheetId + ':checklist'
              const clSectCollapsed = wf.collapsed[clCollapseKey] !== false
              const fp2  = fieldPfx(milestonePhasePrefix)
              const done = Array.isArray(clFiltered) ? clFiltered.filter(i => i.fields[fp2 + ' Complete'] || i.fields[fp2 + ' Not Required']).length : 0
              const tot  = Array.isArray(clFiltered) ? clFiltered.length : 0
              const pct  = tot > 0 ? Math.round(done / tot * 100) : 0
              const gc   = pct === 100 ? '#1D9E75' : pct > 0 ? '#1a6fa8' : 'var(--aj-border)'
              return (
                <div className="wf-node wf-node--section" data-key={clCollapseKey}>
                  <div className="wf-row" onClick={e => e.stopPropagation()}>
                    <button
                      className="wf-triangle"
                      onClick={e => {
                        e.stopPropagation()
                        wf.collapsed[clCollapseKey] = !clSectCollapsed
                        window.dispatchEvent(new Event('outline-render'))
                      }}
                    >
                      <span className={`wf-tri-icon ${clSectCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
                    </button>
                    <button
                      className="wf-bullet wf-bullet--section"
                      style={{ cursor: onSheetZoom ? 'pointer' : 'default' }}
                      onClick={onSheetZoom ? e => { e.stopPropagation(); onSheetZoom(sheetId + ':cl') } : undefined}
                      title={onSheetZoom ? 'Focus on checklist' : undefined}
                    />
                    <div className="wf-text-wrap">
                      <span className="wf-node-text">Checklist</span>
                      {Array.isArray(clFiltered) && clFiltered.length > 0 && (
                        <span className="wf-meta" style={{ marginLeft: 'auto' }}>
                          <span className="wf-cat-bar-wrap">
                            <span className="wf-cat-bar-fill" style={{ width: pct + '%', background: gc }} />
                          </span>
                          <span className="wf-cat-count">{done}/{tot}</span>
                        </span>
                      )}
                    </div>
                    <ActionMenuButton menu={clMenu} />
                  </div>
                  <ActionMenu menu={clMenu}>
                    {!clSelectMode && Array.isArray(clFiltered) && clFiltered.length > 0 && (
                      <ActionMenuItem onClick={() => { clMenu.close(); setClSelectMode(true); setSelectedClIds(new Set()) }}>
                        Select items
                      </ActionMenuItem>
                    )}
                    <ActionMenuItem onClick={() => { clMenu.close(); handleResetChecklist() }}>
                      Reset checklist
                    </ActionMenuItem>
                  </ActionMenu>
                  {!clSectCollapsed && (
                    <div className="wf-children">
                      {(clRawItems === undefined || clRawItems === null) && (
                        <div className="wf-node wf-node--item">
                          <div className="wf-row">
                            <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                            <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                            <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
                            <div className="wf-text-wrap"><span className="wf-node-text" style={{ color: 'var(--aj-text-muted)' }}>Loading…</span></div>
                          </div>
                        </div>
                      )}
                      {Array.isArray(clRawItems) && clFiltered && clFiltered.length === 0 && (
                        <div className="wf-node wf-node--item">
                          <div className="wf-row">
                            <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                            <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                            <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
                            <div className="wf-text-wrap"><span className="wf-node-text" style={{ color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>No checklist items for this phase.</span></div>
                          </div>
                        </div>
                      )}
                      {clFiltered && clFiltered.map(item => (
                        <SheetClItemNode
                          key={item.id}
                          item={item}
                          sheetId={sheetId}
                          phasePrefix={milestonePhasePrefix}
                          currentMemberId={currentMemberId}
                          readOnly={readOnly}
                          selectMode={clSelectMode}
                          isSelected={selectedClIds.has(item.id)}
                          onToggleSelect={id => setSelectedClIds(prev => {
                            const next = new Set(prev)
                            next.has(id) ? next.delete(id) : next.add(id)
                            return next
                          })}
                          onItemCompleted={checkAllComplete}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )
            })()}
            {!readOnly && clSelectMode && selectedClIds.size > 0 && Array.isArray(clFiltered) && (
              <SheetClFloatBar
                selectedIds={selectedClIds}
                items={clFiltered}
                phasePrefix={milestonePhasePrefix}
                currentMemberId={currentMemberId}
                onDone={() => { setClSelectMode(false); setSelectedClIds(new Set()); checkAllComplete() }}
                onCancel={() => { setClSelectMode(false); setSelectedClIds(new Set()) }}
              />
            )}
            {qcPromptEl}
          </>
        )}
        {/* PDF Insert — milestone context only; hidden in Reference/Production branch */}
        {sheet.insertTypeName && context === 'milestone' && (
          <PdfInsertSection sheetId={sheetId} insertTypeName={sheet.insertTypeName} projectId={projectId} />
        )}
      </>
    )
  }

  return (
    <div className={`wf-node wf-node--sheet${isExpanded ? '' : ' wf-node--collapsed'}`} data-key={'sheet:' + sheetId}>
      <div className="wf-row">
        {/* Triangle — expand/collapse */}
        <button
          className="wf-triangle"
          onClick={e => {
            e.stopPropagation()
            wf.collapsed['sheet:' + sheetId] = isExpanded ? true : false
            window.dispatchEvent(new Event('outline-render'))
          }}
        >
          <span className={`wf-tri-icon ${isExpanded ? 'wf-tri-icon--down' : 'wf-tri-icon--right'}`}></span>
        </button>

        {selectMode ? (() => {
          // A preview export (selectMode === 'preview') takes in-progress sheets too.
          const exportable = rowPdfUrl && (selectMode === 'preview' || isSheetExportable(sheet))
          return (
            <input
              type="checkbox"
              checked={isSelected}
              disabled={!exportable}
              onChange={() => exportable && onToggleSelect && onToggleSelect(sheetId)}
              onClick={e => e.stopPropagation()}
              title={!rowPdfUrl ? 'No PDF available' : !exportable ? 'Not yet posted. Use Export preview to include it' : (isSelected ? 'Deselect' : 'Select for export')}
              style={{ cursor: exportable ? 'pointer' : 'not-allowed', flexShrink: 0, marginRight: 2 }}
            />
          )
        })() : (
          <button
            className="wf-bullet wf-bullet--item"
            style={{ cursor: onSheetZoom ? 'pointer' : 'default', background: 'var(--wf-dot)' }}
            onClick={onSheetZoom ? e => { e.stopPropagation(); onSheetZoom(sheetId) } : undefined}
            title={onSheetZoom ? 'Focus on this sheet' : undefined}
            {...(onSheetZoom ? { 'data-kbzoom': '1' } : {})}
          />
        )}

        <div className="wf-text-wrap" style={{ opacity: selectMode && !rowPdfUrl ? 0.45 : 1 }}>
          {/* Sheet number */}
          {numDisplay ? (
            <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 11.5, color: 'var(--aj-text-muted)', marginRight: 5, flexShrink: 0 }}>
              {numDisplay}
            </span>
          ) : null}

          {/* Sheet title — flex:none so PDF icon + QC sit immediately after the text */}
          <span className="wf-node-text wf-node-text--sheet" style={{ flex: 'none' }}>
            {numDisplay ? (titleDisplay || '—') : fallback}
          </span>

          {/* PDF icon — directly after sheet title, flexes with title length. Gated the same way
              as RevisionRow's own icon (2026-07-23): in production/reference context, a sheet with
              no posted revision yet (only prodInProgressRevisions) has nothing official to show —
              isSheetExportable(sheet) is the same posted-check already used to gate export
              selection, reused here so the icon and the checkbox agree on what counts as "real." */}
          {rowPdfUrl && (context !== 'production' || isSheetExportable(sheet)) && (
            <a
              href={rowPdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={async e => {
                e.stopPropagation(); e.preventDefault()
                // Prefer the issued (posted) titleblock-overlay PDF, then the official QC-approved
                // drawing, then the latest PDF. Checked regardless of context (production or
                // milestone) — a sheet's issued PDF is keyed by revision id, not by which view you're
                // browsing from, so Reference/Production must see it too (2026-07-22 fix — this was
                // previously milestone-context-only and silently never showed the stamped PDF from
                // Reference/Production even when the milestone had been posted and burned correctly).
                let url = rowPdfUrl
                const revId = getCurrentRevisionId(sheet)
                try {
                  if (revId) {
                    const m = await getIssuedPdfMap([revId])
                    if (m[revId]) url = m[revId]
                  }
                  if (url === rowPdfUrl) { const r = await getOfficialSheetPdf(sheetId, rowPdfUrl, revId); if (r.url) url = r.url }
                } catch {}
                window.open(url, '_blank', 'noopener,noreferrer')
              }}
              style={{ flexShrink: 0, color: '#c0392b', display: 'flex', alignItems: 'center', padding: '0 2px 0 6px' }}
              title="Open latest QC-approved PDF"
            >
              <PdfIcon />
            </a>
          )}

          {/* QC status — reflects the real QC-tool workflow (Supabase), not legacy flags.
              No PDF = QC can't start yet (sheet still listed for completion tracking). */}
          {context === 'milestone' && (() => {
            if (!rowPdfUrl) return <span title="No PDF uploaded — upload one to start QC" style={{ fontFamily:'"DM Mono",monospace', fontSize:11, color:'#b5651d', marginLeft:4, flexShrink:0 }}>· No PDF</span>
            // Canonical QC status (shared with the QC tool's sheet list) so the outline matches exactly.
            const st = qcSheetStatus(ctx.qcSummaryCache?.[sheet.revisionId])
            if (!st) return null
            return (
              <span style={{ fontFamily:'"DM Mono",monospace', fontSize:11, color: st.color, marginLeft:4, flexShrink:0 }}>
                · {st.label}{st.count ? <span style={{ color: st.countColor, marginLeft: 4 }}>{st.count}</span> : ''}
              </span>
            )
          })()}

          {/* Production: which milestone is currently building this sheet out — posted (done)
              or still in progress — so a sheet with no posted revisions yet doesn't read as idle. */}
          {context === 'production' && (() => {
            const postedRev = Array.isArray(prodPostedRevisions) && prodPostedRevisions.length ? prodPostedRevisions[0] : null
            const rev = postedRev || prodInProgressRevisions[0]
            if (!rev) return null
            const msName = getFirst(rev.fields['Full Deliverable Name (from Deliverables)']) || ''
            if (!msName) return null
            const posted = !!postedRev
            // A sheet can have BOTH a posted release (shown as the name below) AND one or more
            // separate revisions already under way for newer, not-yet-posted milestones at the
            // same time — previously the only way to notice that was to expand the sheet and read
            // its full revision history. Append an hourglass (with a count when more than one)
            // after the posted name so it's visible at a glance from the collapsed row too
            // (Matt, 2026-07-23; extended to a count 2026-08 once a second concurrent in-progress
            // revision was found silently vanishing from the expanded list entirely).
            const inProgressCount = posted ? prodInProgressRevisions.length : Math.max(0, prodInProgressRevisions.length - 1)
            const alsoInProgress = inProgressCount > 0
            return (
              <span
                title={(posted ? 'Posted — ' : 'In progress — ') + msName + (alsoInProgress ? (' (' + inProgressCount + ' more revision' + (inProgressCount > 1 ? 's' : '') + ' also in progress)') : '')}
                style={{
                  fontFamily: '"DM Mono",monospace', fontSize: 11, color: posted ? '#1D9E75' : '#d97706',
                  marginLeft: 4, flexShrink: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
              >
                · {posted ? '✓' : '⏳'} {msName}{alsoInProgress ? ' ⏳' + (inProgressCount > 1 ? '×' + inProgressCount : '') : ''}
              </span>
            )
          })()}

          {/* Flex spacer — pushes all right-side elements to consistent far-right position */}
          <span style={{ flex: '1 1 0', minWidth: 4 }} />

          {/* Right-side elements — checklist count first, then Slack */}

          {/* Checklist progress count — milestone context; always visible (not expand-gated).
              Shows spinner while data is fetching (null = in-flight).
              Shows 0/0 when cached but no items match this phase. */}
          {milestonePhasePrefix && (() => {
            const cached = ctx.loader.get('sheetCl:' + sheetId)
            if (cached === null) {
              // In-flight: show spinner so user knows it's loading
              return <span className="wf-row-spinner" style={{ marginLeft: 6 }} />
            }
            // undefined = not yet requested — show spinner (bulk load will fire shortly)
            if (cached === undefined) {
              return <span className="wf-row-spinner" style={{ marginLeft: 6 }} />
            }
            if (!Array.isArray(cached)) return null
            const fp2   = fieldPfx(milestonePhasePrefix)
            const inPhase = i => {
              const dr = i.fields['Drawing Release (from Deliverable Checklist Item Template)']
              return Array.isArray(dr) ? dr.includes(milestonePhasePrefix) : dr === milestonePhasePrefix
            }
            const tot  = cached.filter(inPhase).length
            const done = cached.filter(i => inPhase(i) && (i.fields[fp2 + ' Complete'] || i.fields[fp2 + ' Not Required'])).length
            const pct  = tot > 0 ? Math.round(done / tot * 100) : 0
            const clr  = pct === 100 ? '#1D9E75' : pct > 0 ? '#1a6fa8' : 'var(--aj-text-muted)'
            return (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 4, flexShrink: 0 }}>
                <span className="wf-cat-bar-wrap">
                  <span className="wf-cat-bar-fill" style={{ width: pct + '%', background: clr }} />
                </span>
                <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: clr }}>
                  {done}/{tot}
                </span>
              </span>
            )
          })()}

          {/* Draft assignment — who's drafting this sheet, for visibility (not a gate). Any
              signed-in team member may set/reassign it; click opens the picker. */}
          {context === 'milestone' && sheet.revisionId && (() => {
            const a = ctx.sheetAssignCache?.[sheet.revisionId]
            return (
              <span
                onClick={e => { e.stopPropagation(); setShowAssignModal(true) }}
                title={a ? 'Assigned to ' + a.memberName + ' — click to reassign' : 'Click to assign a drafter'}
                style={{
                  fontFamily: '"DM Sans",sans-serif', fontSize: 11, marginLeft: 4, flexShrink: 0, cursor: 'pointer',
                  color: a ? 'var(--aj-text-mid)' : 'var(--aj-text-muted)', opacity: a ? 1 : 0.6,
                }}
              >
                · {a ? a.memberName : 'Assign drafter'}
              </span>
            )
          })()}

          {/* Progress pill — not shown in production (not meaningful there) */}
          {context !== 'production' && sheet.progress && (
            <span className="wf-status-pill" style={{ color, borderColor: color + '33' }}>
              {sheet.progress}
            </span>
          )}

          {/* Slack discussion button — last in the right-side group */}
          {projectId && (() => {
            const threadTs   = localThreadTs
            const lastReadTs = threadTs ? localStorage.getItem('slack:read:' + sheetId) : null
            const hasUnread  = !!(threadTs && lastReadTs !== threadTs)
            return (
              <button
                className={`wf-slack-btn${threadTs ? ' wf-slack-btn--active' : ''}`}
                title={threadTs ? 'View discussion' : 'Start a discussion in Slack'}
                onClick={e => { e.stopPropagation(); setShowSlackPanel(true) }}
                style={{ marginLeft: 0 }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                {hasUnread && <span className="wf-slack-unread-dot" />}
              </button>
            )
          })()}
        </div>
        {!readOnly && <ActionMenuButton menu={rowMenu} />}
      </div>
      {!readOnly && <ActionMenu menu={rowMenu}>
        <ActionMenuItem onClick={() => { rowMenu.close(); setShowEditModal(true) }}>Edit Sheet</ActionMenuItem>
        {sheet.revisionId && context === 'milestone' && (
          <ActionMenuItem onClick={() => { rowMenu.close(); setShowAssignModal(true) }}>
            {ctx.sheetAssignCache?.[sheet.revisionId] ? 'Reassign drafter…' : 'Assign drafter…'}
          </ActionMenuItem>
        )}
        <ActionMenuDivider />
        <ActionMenuItem onClick={() => {
          rowMenu.close()
          window.dispatchEvent(new CustomEvent('open-templates-workspace', {
            detail: sheet.templateId
              ? { templateId: sheet.templateId, templateName: sheet.title || '', templateType: 'drawing' }
              : { tab: 'drawing' }
          }))
        }}>Edit template</ActionMenuItem>
        {sheet.revisionId && context === 'milestone' && milestoneId && (
          <>
            <ActionMenuDivider />
            {/* The revision history is fetched when the menu opens (soleVersion), so the tooltip
                states the REAL outcome. The QC scrub is unconditional — any QC work on this
                milestone's version goes with it in every case. */}
            <ActionMenuItem danger
              title={(soleVersion === true
                ? 'This is the sheet’s only version — removing it deletes the Drawing Sheet from the project entirely.'
                : soleVersion === false
                  ? 'Removes this version from this milestone only — the sheet stays in the project.'
                  : 'Removes this version from this milestone; if it’s the sheet’s only version, the Drawing Sheet is deleted from the project entirely.')
                + ' Any QC work on this milestone’s version is deleted with it. You’ll confirm first.'}
              onClick={() => { rowMenu.close(); setShowRemoveModal(true) }}>
              Remove from Milestone
            </ActionMenuItem>
          </>
        )}
        {sheet.revisionId && context === 'milestone' && <ActionMenuDivider />}
        {sheet.revisionId && context === 'milestone' && (
          <ActionMenuItem onClick={() => { rowMenu.close(); openQcTab(sheet, milestonePhasePrefix, milestoneId) }}>
            Open AlderQC
          </ActionMenuItem>
        )}
        {sheet.revisionId && context === 'milestone' && rowPdfUrl && isManagerOrAbove()
          && !ctx.qcSummaryCache?.[sheet.revisionId]?.hasVersion && (
          <ActionMenuItem onClick={() => { rowMenu.close(); handleOverrideApprove() }}>
            Mark as approved (no QC)…
          </ActionMenuItem>
        )}
      </ActionMenu>}
      {/* EditSheetModal — gate-kept edit of sheet number and title */}
      {showEditModal && (
        <EditSheetModal
          sheetNumber={numDisplay}
          sheetTitle={titleDisplay || ''}
          projectId={projectId}
          currentSheetId={sheetId}
          onSave={async (newNum, newTitle, newTemplateId, templateChanged) => {
            const changedNum   = newNum   !== numDisplay
            const changedTitle = newTitle !== (titleDisplay || '')
            if (changedNum || changedTitle || templateChanged) {
              try {
                await updateSheet(sheetId, {
                  ...(changedNum      ? { sheetNumber: newNum }          : {}),
                  ...(changedTitle    ? { sheetTitle:  newTitle }        : {}),
                  ...(templateChanged ? { templateId:  newTemplateId }   : {}),
                })
                if (changedNum)   setLocalNumber(newNum)
                if (changedTitle) setLocalTitle(newTitle)
                window.dispatchEvent(new Event('outline-render'))
                // Number/title/template all feed Airtable's Sheet Number and Title / Drawing
                // Sort Order formulas that groupByDiscipline() sorts by — those recompute
                // server-side, so re-fetch (not hand-patch) both branches a sheet can appear
                // in: the milestone's revision cache and Production's project-wide list.
                if (milestoneId && ctx.refreshMsSheets) ctx.refreshMsSheets(milestoneId)
                window.dispatchEvent(new Event('aj-refresh-proj-sheets'))
              } catch (err) {
                console.error('[SheetRow] edit sheet error', err)
              }
            }
            // Reset checklist when template changed (runs regardless of other field changes)
            if (templateChanged) {
              // Update sheetDiscCache immediately so "Edit Template" action menu has the right ID
              // without waiting for the async getProjectSheets re-fetch.
              if (ctx.sheetDiscCache) {
                ctx.sheetDiscCache[sheetId] = { ...(ctx.sheetDiscCache[sheetId] || {}), templateId: newTemplateId || '' }
              }
              ctx.loader.reset('sheetCl:' + sheetId)
              if (ctx.sheetClCache) ctx.sheetClCache[sheetId] = null
              window.dispatchEvent(new Event('outline-render'))
              try {
                await resetSheetChecklist(sheetId)
              } catch (err) {
                console.error('[SheetRow] reset sheet checklist error', err)
              }
              maybeLoadChecklist()
            }
            setShowEditModal(false)
          }}
          onCancel={() => setShowEditModal(false)}
        />
      )}

      {/* AssignDrafterModal — visibility-only draft assignment, scoped to this revision */}
      {showAssignModal && sheet.revisionId && (
        <AssignDrafterModal
          assignedMemberId={ctx.sheetAssignCache?.[sheet.revisionId]?.memberAirtableId || ''}
          assignedMemberName={ctx.sheetAssignCache?.[sheet.revisionId]?.memberName || ''}
          onSave={async member => {
            try {
              const actor = ctx.currentMember
              const actorName = actor?.fields?.['Name'] || actor?.fields?.['Member Name'] || actor?.fields?.['Full Name'] || ''
              const saved = await setSheetDraftAssignment(
                sheet.revisionId, sheetId, member.id, member.name,
                actor?.id, actorName
              )
              if (ctx.sheetAssignCache) ctx.sheetAssignCache[sheet.revisionId] = saved
              window.dispatchEvent(new CustomEvent('sheet-assignment-updated'))
            } catch (err) {
              console.error('[SheetRow] assign drafter error', err)
              alertDialog({ title: 'Could not assign', message: err.message || 'Something went wrong.' })
            }
            setShowAssignModal(false)
          }}
          onClear={async () => {
            try {
              await clearSheetDraftAssignment(sheet.revisionId)
              if (ctx.sheetAssignCache) delete ctx.sheetAssignCache[sheet.revisionId]
              window.dispatchEvent(new CustomEvent('sheet-assignment-updated'))
            } catch (err) {
              console.error('[SheetRow] clear drafter error', err)
              alertDialog({ title: 'Could not clear assignment', message: err.message || 'Something went wrong.' })
            }
            setShowAssignModal(false)
          }}
          onCancel={() => setShowAssignModal(false)}
        />
      )}

      {/* SlackThreadPanel outside wf-row — same pattern as MilestoneRow */}
      {showSlackPanel && projectId && (
        <SlackThreadPanel
          entityId={sheetId}
          entityType="sheet"
          entityName={sheet.title || sheet.sheetTitle || 'Drawing Sheet'}
          projectId={projectId}
          projectName={projectName || ''}
          threadTs={localThreadTs}
          onClose={() => setShowSlackPanel(false)}
          onThreadStarted={ts => {
            setLocalThreadTs(ts)
            if (ctx.sheetTsCache) ctx.sheetTsCache[sheetId] = ts
          }}
        />
      )}
      {/* RemoveSheetModal — delete this revision (and optionally the Drawing Sheet) */}
      {showRemoveModal && sheet.revisionId && milestoneId && (
        <RemoveSheetModal
          sheet={sheet}
          milestoneId={milestoneId}
          onClose={() => setShowRemoveModal(false)}
          onRemoved={({ sheetAlsoDeleted }) => {
            setShowRemoveModal(false)
            // Reload the milestone's sheet list — the sheet (or just this revision) is gone
            if (ctx.refreshMsSheets) ctx.refreshMsSheets(milestoneId)
            // If the Drawing Sheet record itself was deleted, remove it from production cache too
            if (sheetAlsoDeleted && ctx.projSheetCache) {
              ctx.projSheetCache = ctx.projSheetCache.filter(s => s.id !== sheetId)
            }
            window.dispatchEvent(new Event('outline-render'))
          }}
        />
      )}

      {/* ── Expanded content ────────────────────────────────────────────── */}
      {isExpanded && (
        <div className="wf-children">

          {/* PRODUCTION: posted revisions only, most recent first */}
          {context === 'production' && (
            <>
              {(prodPostedRevisions === undefined || prodPostedRevisions === null) && (
                <div className="wf-loading" style={{ padding: '5px 8px 5px 49px', fontSize: 12 }}>Loading revisions...</div>
              )}
              {Array.isArray(prodPostedRevisions) && prodPostedRevisions.length === 0 && !prodInProgressRevisions.length && (
                <div className="wf-loading" style={{ padding: '5px 8px 5px 49px', fontSize: 12, color: 'var(--aj-text-muted)' }}>No posted revisions yet</div>
              )}
              {/* Currently building — shown even with zero posted revisions, so it's never a mystery
                  which milestone(s) a brand-new sheet is on. EVERY unposted revision, not just one —
                  a sheet carried forward into two still-open milestones at once used to have the
                  second silently vanish from Production here (Matt, 2026-08). */}
              {prodInProgressRevisions.map(rev => (
                <RevisionRow
                  key={'inprogress-' + rev.id}
                  revision={rev}
                  isLatest={false}
                  showMilestone={true}
                  revisionId={rev.id}
                  inProgressBadge
                />
              ))}
              {Array.isArray(prodPostedRevisions) && prodPostedRevisions.map((rev, i) => (
                <RevisionRow
                  key={rev.id}
                  revision={rev}
                  isLatest={i === 0}
                  showMilestone={true}
                  revisionId={rev.id}
                />
              ))}
            </>
          )}

          {/* MILESTONE: single revision for this milestone + phase checklist */}
          {context === 'milestone' && (
            <>
              {msRevision && (
                <RevisionRow
                  key={msRevision.id}
                  revision={msRevision}
                  isLatest={false}
                  milestonePhasePrefix={milestonePhasePrefix}
                  showMilestone={false}
                  revisionId={msRevision.id}
                  canManagePdf
                />
              )}

              {milestonePhasePrefix && (() => {
                const clCollapsed = wf.collapsed['sheet:' + sheetId + ':checklist'] !== false
                return (
                  <div className="wf-node wf-node--section" data-key={'sheet:' + sheetId + ':checklist'}>
                    <div className="wf-row" onClick={e => e.stopPropagation()}>
                      {/* tri column — collapse toggle */}
                      <button
                        className="wf-triangle"
                        onClick={e => {
                          e.stopPropagation()
                          wf.collapsed['sheet:' + sheetId + ':checklist'] = !clCollapsed
                          window.dispatchEvent(new Event('outline-render'))
                        }}
                      >
                        <span className={`wf-tri-icon ${clCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
                      </button>
                      <button
                        className="wf-bullet wf-bullet--section"
                        style={{ cursor: onSheetZoom ? 'pointer' : 'default' }}
                        onClick={onSheetZoom ? e => { e.stopPropagation(); onSheetZoom(sheetId + ':cl') } : undefined}
                        title={onSheetZoom ? 'Focus on Checklist' : undefined}
                      />
                      <div className="wf-text-wrap">
                        <span className="wf-node-text">Checklist</span>
                        {Array.isArray(clFiltered) && clFiltered.length > 0 && (() => {
                          const fp2  = fieldPfx(milestonePhasePrefix)
                          const done = clFiltered.filter(i => i.fields[fp2 + ' Complete'] || i.fields[fp2 + ' Not Required']).length
                          const tot  = clFiltered.length
                          const pct  = tot > 0 ? Math.round(done / tot * 100) : 0
                          const gc   = pct === 100 ? '#1D9E75' : pct > 0 ? '#1a6fa8' : 'var(--aj-border)'
                          return (
                            <span className="wf-meta" style={{ marginLeft: 'auto' }}>
                              <span className="wf-cat-bar-wrap">
                                <span className="wf-cat-bar-fill" style={{ width: pct + '%', background: gc }} />
                              </span>
                              <span className="wf-cat-count">{done}/{tot}</span>
                            </span>
                          )
                        })()}
                      </div>
                      {/* ⋯ section action menu */}
                      <ActionMenuButton menu={clMenu} />
                    </div>
                    {/* ActionMenu for the Checklist section header ⋯ — portal, position:fixed */}
                    <ActionMenu menu={clMenu}>
                      {!clSelectMode && Array.isArray(clFiltered) && clFiltered.length > 0 && (
                        <ActionMenuItem onClick={() => { clMenu.close(); setClSelectMode(true); setSelectedClIds(new Set()) }}>
                          Select items
                        </ActionMenuItem>
                      )}
                      <ActionMenuItem onClick={() => { clMenu.close(); handleResetChecklist() }}>
                        Reset checklist
                      </ActionMenuItem>
                    </ActionMenu>

                    {!clCollapsed && (
                      <div className="wf-children">
                        {(clRawItems === undefined || clRawItems === null) && (
                          <div className="wf-node wf-node--item">
                            <div className="wf-row">
                              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                              <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
                              <div className="wf-text-wrap">
                                <span className="wf-node-text" style={{ color: 'var(--aj-text-muted)' }}>Loading…</span>
                              </div>
                            </div>
                          </div>
                        )}
                        {Array.isArray(clRawItems) && clFiltered && clFiltered.length === 0 && (
                          <div className="wf-node wf-node--item">
                            <div className="wf-row">
                              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                              <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                              <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }}></span>
                              <div className="wf-text-wrap">
                                <span className="wf-node-text" style={{ color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>No checklist items for this phase.</span>
                              </div>
                            </div>
                          </div>
                        )}
                        {clFiltered && clFiltered.map(item => (
                          <SheetClItemNode
                            key={item.id}
                            item={item}
                            sheetId={sheetId}
                            phasePrefix={milestonePhasePrefix}
                            currentMemberId={currentMemberId}
                            readOnly={readOnly}
                            selectMode={clSelectMode}
                            isSelected={selectedClIds.has(item.id)}
                            onToggleSelect={id => setSelectedClIds(prev => {
                              const next = new Set(prev)
                              next.has(id) ? next.delete(id) : next.add(id)
                              return next
                            })}
                            onItemCompleted={checkAllComplete}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )
              })()}

              {/* Bulk float bar — rendered outside the section node, position:fixed */}
              {!readOnly && clSelectMode && selectedClIds.size > 0 && Array.isArray(clFiltered) && (
                <SheetClFloatBar
                  selectedIds={selectedClIds}
                  items={clFiltered}
                  phasePrefix={milestonePhasePrefix}
                  currentMemberId={currentMemberId}
                  onDone={() => { setClSelectMode(false); setSelectedClIds(new Set()); checkAllComplete() }}
                  onCancel={() => { setClSelectMode(false); setSelectedClIds(new Set()) }}
                />
              )}
              {qcPromptEl}
            </>
          )}

          {/* PDF Insert — milestone context only; hidden in Reference/Production branch */}
          {sheet.insertTypeName && context === 'milestone' && (
            <PdfInsertSection sheetId={sheetId} insertTypeName={sheet.insertTypeName} projectId={projectId} />
          )}
        </div>
      )}
    </div>
  )
}

/* ── DisciplineGroup ─────────────────────────────────────────────────────── */
function DisciplineGroup({ discipline, items, collapsed, onToggle, groupKey, onDisciplineZoom, onSheetZoom, context, milestonePhasePrefix, currentMemberId, milestoneId, readOnly, selectMode, selectedIds, onToggleSelect, onSelectAll, projectId, projectName }) {
  const discMenu = useActionMenu()

  // Done = QC-approved. Only meaningful in milestone context — that's the only place
  // ctx.qcSummaryCache gets populated (keyed by Sheet Revision id, i.e. item.revisionId here).
  const discPct = context === 'milestone' ? (() => {
    const done = items.filter(s => {
      const sm = ctx.qcSummaryCache?.[s.revisionId]
      return !!(sm && (sm.approved || sm.status === 'approved'))
    }).length
    const total = items.length
    return { done, total, pct: total > 0 ? Math.round(done / total * 100) : 0 }
  })() : null
  const discBarColor = discPct && (discPct.pct === 100 ? '#1D9E75' : discPct.pct > 0 ? '#1a6fa8' : 'var(--aj-border)')

  // Expand/collapse all sheet rows within this discipline group
  function expandAllSheets() {
    discMenu.close()
    items.forEach(s => { wf.collapsed['sheet:' + s.id] = false })
    window.dispatchEvent(new Event('outline-render'))
  }
  function collapseAllSheets() {
    discMenu.close()
    items.forEach(s => { wf.collapsed['sheet:' + s.id] = true })
    window.dispatchEvent(new Event('outline-render'))
  }

  return (
    <div className={`wf-node wf-node--section wf-node--section-disc${collapsed ? ' wf-node--collapsed' : ''}`} data-key={groupKey}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={onToggle}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button
          className="wf-bullet wf-bullet--section"
          title={'Focus on ' + discipline}
          onClick={onDisciplineZoom ? () => onDisciplineZoom(discipline) : undefined}
          style={{ cursor: onDisciplineZoom ? 'pointer' : 'default' }}
          {...(onDisciplineZoom ? { 'data-kbzoom': '1' } : {})}
        ></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--section">{discipline}</span>
          {discPct ? (
            <span className="wf-meta">
              <span className="wf-cat-bar-wrap">
                <span className="wf-cat-bar-fill" style={{ width: discPct.pct + '%', background: discBarColor }} />
              </span>
              <span className="wf-cat-count">{discPct.done}/{discPct.total}</span>
            </span>
          ) : (
            <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: '8px' }}>{items.length}</span>
          )}
          {selectMode && onSelectAll && (
            <button
              onClick={e => { e.stopPropagation(); onSelectAll() }}
              style={{
                fontSize: 10, fontFamily: '"DM Sans", sans-serif', background: 'transparent',
                border: 'none', cursor: 'pointer', color: '#1a6fa8', padding: '0 0 0 8px',
              }}
            >all</button>
          )}
        </div>
        {/* ⋯ discipline action menu button */}
        <ActionMenuButton menu={discMenu} />
      </div>
      {/* ActionMenu renders via portal — position:fixed, anchored to the ⋯ button.
          Discipline-level menu acts on sheet rows within this group. "Select sheets
          for export" lives at the Drawing Sheets section header level, not here. */}
      <ActionMenu menu={discMenu}>
        <ActionMenuItem onClick={expandAllSheets}>Expand all</ActionMenuItem>
        <ActionMenuItem onClick={collapseAllSheets}>Collapse all</ActionMenuItem>
      </ActionMenu>
      {!collapsed && (
        <div className="wf-children">
          {items.map(s => (
            <SheetRow
              key={s.id}
              sheet={s}
              context={context}
              milestonePhasePrefix={milestonePhasePrefix}
              currentMemberId={currentMemberId}
              milestoneId={milestoneId}
              readOnly={readOnly}
              selectMode={selectMode}
              isSelected={selectedIds ? selectedIds.has(s.id) : false}
              onToggleSelect={onToggleSelect}
              projectId={projectId}
              projectName={projectName}
              onSheetZoom={onSheetZoom}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/* ── Main component ─────────────────────────────────────────────────────── */

/*
 * sheets:               [{id, title, sheetNumber, sheetTitle, progress, slackThreadTs?, revision?, revisionId?}]
 * collapseKeyPrefix:    string — collapse key per discipline = prefix + ':' + discipline
 * context:              'production' | 'milestone'  (default: 'production')
 * milestonePhasePrefix: string | null — e.g. 'DD', 'PPC / BID' (milestone context)
 * currentMemberId:      string | null — for checklist write operations
 * onDisciplineZoom:     (discipline: string) => void | null — callback when discipline bullet clicked
 * filterDiscipline:     string | null — when set, render only this discipline group
 * projectId:            string | null — Airtable Project record ID; enables Slack discussion buttons
 * projectName:          string | null — used in the Slack opening message
 */
export default function DrawingSheetList({
  sheets,
  collapseKeyPrefix,
  context = 'production',
  milestonePhasePrefix = null,
  currentMemberId = null,
  milestoneId = null,
  readOnly = false,
  onDisciplineZoom = null,
  onSheetZoom = null,
  filterDiscipline = null,
  projectId = null,
  projectName = null,
}) {
  const [, forceUpdate]  = useReducer(v => v + 1, 0)
  const [selectMode,     setSelectMode]  = useState(false)   // false | 'export' | 'preview' (AT-8Y7XZ)
  const [selectedIds,    setSelectedIds] = useState(() => new Set())
  const [exporting,      setExporting]   = useState(false)
  const [exportError,    setExportError] = useState(null)
  const [exportNote,     setExportNote]  = useState(null)   // non-error notice (e.g. fell back to latest)

  // Subscribe to outline-render so sheet expansion state and cache updates propagate.
  // Also schedule two timed re-checks after mount — catches cases where the outline-render
  // cascade is missed due to race conditions between bulk-fetch completion and component mounting.
  useEffect(() => {
    window.addEventListener('outline-render', forceUpdate)
    const t1 = setTimeout(forceUpdate, 800)   // catch early bulk-fetch completions
    const t2 = setTimeout(forceUpdate, 2500)  // catch slow network / large batches
    return () => {
      window.removeEventListener('outline-render', forceUpdate)
      clearTimeout(t1)
      clearTimeout(t2)
    }
  }, [])

  // ── QC status (milestone context) — batch-load real QC-tool summaries into ctx cache,
  // keyed by Sheet Revision ID so each milestone instance shows only its own QC.
  // Re-fetches when the revision set changes and whenever the QC tool signals an update.
  const revIdKey = (sheets || []).map(s => s.revisionId).filter(Boolean).join(',')
  useEffect(() => {
    if (context !== 'milestone') return
    const ids = [...new Set((sheets || []).map(s => s.revisionId).filter(Boolean))]
    if (!ids.length) return
    let cancelled = false
    const load = () => getRevisionQcSummaries(ids).then(sum => {
      if (cancelled) return
      ctx.qcSummaryCache = Object.assign(ctx.qcSummaryCache || {}, sum)
      forceUpdate()
      // Also tell everyone else — the Drawing Sheets / discipline group headers (MilestoneSections.jsx,
      // this file's own DisciplineGroup) read the same cache for their approved/total counts and only
      // re-render on outline-render, not on this component's own local forceUpdate.
      window.dispatchEvent(new Event('outline-render'))
    }).catch(() => {})
    load()
    window.addEventListener('qc-updated', load)
    return () => { cancelled = true; window.removeEventListener('qc-updated', load) }
  }, [context, revIdKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Drafting assignments (milestone context) — who's assigned to draft each sheet, for
  // visibility only (docs/QC_WORKFLOW.md). Batch-loaded into ctx cache keyed by Sheet Revision
  // ID, same shape as the QC summary load above. Re-fetches on the revision set changing and
  // whenever a row's own assign/clear action fires 'sheet-assignment-updated'.
  useEffect(() => {
    if (context !== 'milestone') return
    const ids = [...new Set((sheets || []).map(s => s.revisionId).filter(Boolean))]
    if (!ids.length) return
    let cancelled = false
    const load = () => getSheetDraftAssignments(ids).then(a => {
      if (cancelled) return
      ctx.sheetAssignCache = Object.assign(ctx.sheetAssignCache || {}, a)
      forceUpdate()
      window.dispatchEvent(new Event('outline-render'))
    }).catch(() => {})
    load()
    window.addEventListener('sheet-assignment-updated', load)
    return () => { cancelled = true; window.removeEventListener('sheet-assignment-updated', load) }
  }, [context, revIdKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Bulk checklist load — DataLoader handles deduplication ────────────────
  // The parent (MilestoneSections) also fires bulk loads, but in some zoom contexts
  // (phase zoom, schedule zoom, project home) the parent load doesn't reliably reach
  // this component. By loading here too, we guarantee counts are always populated.
  // DataLoader deduplicates: if MilestoneSections already marked IDs in-flight, these
  // calls return immediately without firing duplicate API requests.
  // Key on the sorted sheet ID list so this re-fires only when the sheet set changes.
  const sheetIdsKey = context === 'milestone' && milestonePhasePrefix
    ? sheets.map(s => s.id).sort().join(',')
    : ''
  useEffect(() => {
    if (!sheetIdsKey) return
    const sheetIds = sheets.map(s => s.id).filter(Boolean)
    if (!sheetIds.length) return

    // Checklist bulk load — DataLoader sets null sentinels atomically, deduplicates,
    // dispatches outline-render on completion.
    ctx.loader.loadBatch(sheetIds, id => 'sheetCl:' + id,
      ids => getProjectSheetChecklistItems(ids))
  }, [sheetIdsKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // Enter select mode via the Drawing Sheets section header ⋯ → "Select sheets for export".
  // Production context fires enter-select-sheets with no detail (matches any instance).
  // Milestone context fires with detail.collapseKeyPrefix to target the correct instance —
  // required because multiple DrawingSheetList instances may be mounted simultaneously
  // (one per expanded milestone).
  useEffect(() => {
    function onEnterSelect(e) {
      if (e.detail?.collapseKeyPrefix && e.detail.collapseKeyPrefix !== collapseKeyPrefix) return
      // detail.preview: a PREVIEW set of the milestone's in-progress drawings, before posting —
      // unposted sheets are selectable and every page is stamped PREVIEW / NOT ISSUED.
      setSelectMode(e.detail?.preview ? 'preview' : 'export')
      setSelectedIds(new Set())
      setExportError(null)
    }
    window.addEventListener('enter-select-sheets', onEnterSelect)
    return () => window.removeEventListener('enter-select-sheets', onEnterSelect)
  }, [collapseKeyPrefix])

  function toggleDisc(discipline) {
    const key = collapseKeyPrefix + ':' + discipline
    wf.collapsed[key] = !(wf.collapsed[key] === true)
    saveViewState()
    forceUpdate()
  }

  function toggleSelect(sheetId) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(sheetId) ? next.delete(sheetId) : next.add(sheetId)
      return next
    })
  }

  function enterSelectMode() {
    setSelectMode('export')
    setSelectedIds(new Set())
    setExportError(null)
    setExportNote(null)
  }

  function exitSelectMode() {
    setSelectMode(false)
    setSelectedIds(new Set())
    setExportError(null)
  }

  // Resolve PDF URL for a sheet — checks revision directly on sheet first (milestone
  // context passes the revision record on sheet.revision), then falls back to
  // sheetRevCache (production context).
  function getSheetPdfUrl(s) {
    if (s?.revision) return getPdfUrl(s.revision)
    const rev = ctx.sheetRevCache?.[s.id]?.[0]
    return rev ? getPdfUrl(rev) : null
  }

  const isPreview = selectMode === 'preview'

  function selectAllInGroup(items) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      items.forEach(s => { if (getSheetPdfUrl(s) && (isPreview || isSheetExportable(s))) next.add(s.id) })
      return next
    })
  }

  function selectAll() {
    setSelectedIds(prev => {
      const next = new Set(prev)
      ;(sheets || []).forEach(s => { if (getSheetPdfUrl(s) && (isPreview || isSheetExportable(s))) next.add(s.id) })
      return next
    })
  }

  async function handleExport() {
    if (exporting) return
    setExportError(null); setExportNote(null)

    // Discipline-grouped/ordered (same groupByDiscipline() the on-screen Sheet List uses, so
    // the export always matches what's displayed), then within each group in sheet-sort order.
    // Prefer each sheet's official QC-approved drawing; fall back to its latest revision PDF
    // when no approved version exists yet.
    const ordered = groupByDiscipline((sheets || []).filter(s => selectedIds.has(s.id)))
      .flatMap(g => g.items)

    // See findFullyApprovedUnpostedSibling's comment above — milestone-scoped exports only;
    // Production's project-wide export already sees everything, so "the other milestone" isn't
    // a meaningful question there.
    if (context === 'milestone' && milestoneId && !isPreview) {
      let sibling = null
      try { sibling = await findFullyApprovedUnpostedSibling(milestoneId, new Set(ordered.map(s => s.id))) } catch { /* best-effort */ }
      if (sibling) {
        const proceed = await confirmDialog({
          title: 'Newer approved set found',
          message: '"' + sibling.name + '" hasn’t been posted yet, but all ' + sibling.count + ' of its drawing sheets are already QC-approved. Did you mean to post and export that milestone instead?',
          confirmLabel: 'Export from here anyway',
          cancelLabel: 'Cancel',
        })
        if (!proceed) return
      }
    }

    setExporting(true)
    try {
      // Issued (posted) titleblock-overlay PDFs take priority — one batch call. Checked regardless
      // of context: a sheet's issued PDF is keyed by revision id, not by which view (production or
      // milestone) the export was triggered from — see the single-sheet open handler above for the
      // matching 2026-07-22 fix (this was previously milestone-context-only, so Reference/Production
      // exports never picked up the stamped PDF even for a correctly posted-and-burned milestone).
      let issuedMap = {}
      try {
        const revIds = ordered.map(s => getCurrentRevisionId(s)).filter(Boolean)
        if (revIds.length) issuedMap = await getIssuedPdfMap(revIds)
      } catch { /* fall back to QC/latest */ }
      const resolved = await Promise.all(ordered.map(async s => {
        const revId = getCurrentRevisionId(s)
        if (revId && issuedMap[revId]) return { url: issuedMap[revId], approved: true }
        const fallback = getSheetPdfUrl(s)
        const r = await getOfficialSheetPdf(s.id, fallback, revId)
        return { url: r.url, approved: r.isQcApproved }
      }))
      const urls = resolved.map(r => r.url).filter(Boolean)
      if (urls.length === 0) { setExportError('No PDFs available for selected sheets'); setExporting(false); return }
      const fellBack = resolved.filter(r => r.url && !r.approved).length

      const token = await window.__clerkGetToken()
      const res = await fetch('/api/merge-pdfs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify({ urls, filename: isPreview ? 'Drawing-Set-Preview.pdf' : 'Drawing-Set.pdf', preview: isPreview }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || 'Export failed (' + res.status + ')')
      }
      const blob = await res.blob()
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = isPreview ? 'Drawing-Set-Preview.pdf' : 'Drawing-Set.pdf'
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(link.href)
      if (fellBack > 0 && !isPreview) setExportNote(fellBack + ' sheet' + (fellBack !== 1 ? 's' : '') + ' not yet QC-approved — exported the latest drawing instead.')
      exitSelectMode()
    } catch (err) {
      setExportError(err.message || 'Export failed')
    } finally {
      setExporting(false)
    }
  }

  const groups = groupByDiscipline(sheets || [])

  // Count how many selected sheets have a PDF available
  const selectedWithPdf = [...selectedIds].filter(id => {
    const s = (sheets || []).find(sh => sh.id === id)
    return s ? !!getSheetPdfUrl(s) : false
  }).length
  const allHavePdf = selectedIds.size > 0 && selectedWithPdf === selectedIds.size

  // ── Bug 1: Discipline zoom — Section zoom render rule ─────────────────────
  // When filterDiscipline is set, ZoomTitle already shows the discipline name.
  // Rendering DisciplineGroup would produce a duplicate header. Instead, bypass
  // DisciplineGroup entirely and render the matching group's SheetRows directly.
  if (filterDiscipline) {
    const match = groups.find(g => g.discipline === filterDiscipline)
    if (!match) return null
    return (
      <>
        {match.items.map(s => (
          <SheetRow
            key={s.id}
            sheet={s}
            context={context}
            milestonePhasePrefix={milestonePhasePrefix}
            currentMemberId={currentMemberId}
            milestoneId={milestoneId}
            readOnly={readOnly}
            onSheetZoom={onSheetZoom}
            selectMode={selectMode}
            isSelected={selectedIds ? selectedIds.has(s.id) : false}
            onToggleSelect={toggleSelect}
            projectId={projectId}
            projectName={projectName}
          />
        ))}
      </>
    )
  }

  return (
    <>
      {groups.map(({ discipline, items }) => {
        const key       = collapseKeyPrefix + ':' + discipline
        const collapsed = wf.collapsed[key] === true  // default: expanded
        return (
          <DisciplineGroup
            key={discipline}
            discipline={discipline}
            items={items}
            collapsed={collapsed}
            groupKey={key}
            onToggle={() => toggleDisc(discipline)}
            onDisciplineZoom={onDisciplineZoom}
            onSheetZoom={onSheetZoom}
            context={context}
            milestonePhasePrefix={milestonePhasePrefix}
            currentMemberId={currentMemberId}
            milestoneId={milestoneId}
            readOnly={readOnly}
            selectMode={selectMode}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onSelectAll={() => selectAllInGroup(items)}
            projectId={projectId}
            projectName={projectName}
          />
        )
      })}

      {/* Export notice — e.g. some sheets fell back to the latest (non-approved) drawing */}
      {exportNote && (
        <div className="aj-float-bar" style={{ position: 'fixed', bottom: 0, right: 0, zIndex: 200, background: '#3a2a10', color: '#f0c890', display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', transition: 'left 0.22s ease' }}>
          <span style={{ fontSize: 12, fontFamily: '"DM Sans", sans-serif', flex: 1 }}>{exportNote}</span>
          <button onClick={() => setExportNote(null)} style={{ fontSize: 11, fontFamily: '"DM Sans", sans-serif', background: 'transparent', border: 'none', cursor: 'pointer', color: '#f0c890', padding: 0 }}>Dismiss</button>
        </div>
      )}

      {/* Floating export bar — visible whenever selectMode is active so Cancel is always reachable */}
      {selectMode && (
        <div style={{
          position: 'fixed', bottom: 0, right: 0,
          zIndex: 200,
          background: '#07272D', color: '#fff',
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 20px',
          transition: 'left 0.22s ease',
        }}
          className="aj-float-bar"
        >
          <span style={{ fontSize: 12, fontFamily: '"DM Sans", sans-serif', flex: 1 }}>
            {selectedIds.size > 0
              ? `${selectedIds.size} sheet${selectedIds.size !== 1 ? 's' : ''} selected`
              : isPreview
                ? 'Select sheets for a preview PDF (in-progress drawings, stamped PREVIEW)'
                : 'Select sheets to export as a combined PDF'}
          </span>
          {exportError && (
            <span style={{ fontSize: 11, color: '#ff9999', fontFamily: '"DM Sans", sans-serif' }}>
              {exportError}
            </span>
          )}
          {selectedIds.size > 0 && !allHavePdf && !exportError && (
            <span style={{ fontSize: 11, color: '#aabbcc', fontFamily: '"DM Sans", sans-serif' }}>
              Some sheets have no PDF
            </span>
          )}
          {selectedIds.size > 0 && (
            <>
              <button
                onClick={selectAll}
                style={{
                  fontSize: 11, fontFamily: '"DM Sans", sans-serif', background: 'transparent',
                  border: 'none', cursor: 'pointer', color: '#aabbcc', padding: 0,
                }}
              >Select all</button>
              <button
                onClick={() => setSelectedIds(new Set())}
                style={{
                  fontSize: 11, fontFamily: '"DM Sans", sans-serif', background: 'transparent',
                  border: 'none', cursor: 'pointer', color: '#aabbcc', padding: 0,
                }}
              >Clear</button>
            </>
          )}
          <button
            onClick={exitSelectMode}
            style={{
              fontSize: 11, fontFamily: '"DM Sans", sans-serif', background: 'transparent',
              border: 'none', cursor: 'pointer', color: '#ff9999', padding: 0,
            }}
          >✕ Cancel</button>
          {selectedIds.size > 0 && (
            <button
              onClick={handleExport}
              disabled={exporting}
              style={{
                fontSize: 12, fontFamily: '"DM Sans", sans-serif',
                background: allHavePdf ? '#1D9E75' : '#3a5a68',
                color: '#fff', border: 'none', borderRadius: 4,
                cursor: allHavePdf ? 'pointer' : 'not-allowed',
                padding: '5px 14px',
                opacity: exporting ? 0.7 : 1,
              }}
            >
              {exporting ? 'Exporting…' : isPreview ? 'Export preview' : 'Export PDF'}
            </button>
          )}
        </div>
      )}

    </>
  )
}
